import React, { useState, useEffect, useCallback } from 'react';
import {
  MagnifyingGlass,
  Funnel,
  Plus,
  Eye,
  Calendar,
  Printer,
  X,
  Export,
  FileCsv,
  FileXls,
  FilePdf,
  ArrowsClockwise,
  Percent,
  Tag,
  CheckCircle,
  XCircle,
  Clock,
  Truck,
  Phone,
  MapPin,
  CurrencyInr,
  Note,
  PencilSimple,
  FloppyDisk,
  CaretLeft,
  CaretRight,
  WhatsappLogo,
  DeviceMobile,
  Storefront
} from '@phosphor-icons/react';
import { Header } from '../../components/layout';
import { Modal, Button } from '../../components/common';
import { orderService, adminService, slotService, serviceService, customerService, addonService } from '../../services';
import { hasPermission } from '../../utils/permissions';
import './Orders.css';

const orderStatuses = ['New', 'Cancelled', 'Pick-up Pending', 'In-Progress', 'Delivery Pending', 'Delivered'];
const paymentStatuses = ['Pending', 'Confirmed'];
const serviceTypes = ['Standard', 'Semi Express', 'Express'];
// Fallback garment tabs shown when the selected service has no subcategories
// (non-PerItem services, or seed data without a defined category set).
const garmentCategories = ['Men', 'Women', 'Kids', 'Shoes', 'Boots', 'Sandals', 'Slippers', 'Others', 'Household'];

// Helper function to get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  return new Date().toISOString().split('T')[0];
};

// Helper function to compute expected status based on dates
const computeLiveStatus = (currentStatus, pickupDate, deliveryDate) => {
  const today = getTodayDate();

  // Skip if already in terminal states
  if (currentStatus === 'Cancelled' || currentStatus === 'Delivered') {
    return currentStatus;
  }

  // If today is pickup date or past it, and status is "New" -> "Pick-up Pending"
  if (pickupDate && today >= pickupDate && currentStatus === 'New') {
    return 'Pick-up Pending';
  }

  // If today is delivery date or past it, and status is "In-Progress" -> "Delivery Pending"
  if (deliveryDate && today >= deliveryDate && currentStatus === 'In-Progress') {
    return 'Delivery Pending';
  }

  return currentStatus;
};

export function Orders({ onMenuClick }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleSlots, setRescheduleSlots] = useState([]);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportRange, setExportRange] = useState({ startDate: '', endDate: '' });
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [rescheduleData, setRescheduleData] = useState({ pickupDate: '', pickupSlot: '', deliveryDate: '' });
  const [discountData, setDiscountData] = useState({ type: 'percent', value: '', code: '' });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [ordersTotalCount, setOrdersTotalCount] = useState(0);
  const ITEMS_PER_PAGE = 20;

  // Debounce the search box so we don't hit the backend on every keystroke.
  // Search must go to the backend (not just filter the current page's 20
  // orders) so an order sitting on another page is actually found.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery]);

  // Server-side summary stats (accurate across all pages)
  const [serverActiveTotal, setServerActiveTotal] = useState(0);
  const [serverOrderBookRevenue, setServerOrderBookRevenue] = useState(0);
  const [serverOrderBookDiscount, setServerOrderBookDiscount] = useState(0);

  // Staff and slots data
  const [staffList, setStaffList] = useState([]);
  const [, setPickupSlotsList] = useState([]);
  const [pickupSlotsFullList, setPickupSlotsFullList] = useState([]);

  // Create Order Modal State
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [createPhone, setCreatePhone] = useState('');
  const [createCustomer, setCreateCustomer] = useState(null);
  const [createPhoneLookupLoading, setCreatePhoneLookupLoading] = useState(false);
  const [createPhoneError, setCreatePhoneError] = useState('');
  const [createNewCustomerName, setCreateNewCustomerName] = useState('');
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [createOrderFormData, setCreateOrderFormData] = useState({
    orderSource: 'Call', isPickupScheduled: true, pickupDate: '', pickupSlot: '',
    deliveryDate: '', serviceCategory: '', serviceType: 'Standard', deliveryType: 'Home Delivery',
    selectedAddressIdx: 0
  });
  const [createOrderServices, setCreateOrderServices] = useState([]);
  const [createOrderSlots, setCreateOrderSlots] = useState([]);
  const [createOrderItemQtys, setCreateOrderItemQtys] = useState({});
  const [createOrderGarmentCat, setCreateOrderGarmentCat] = useState('Men');
  const [createOrderItemSearch, setCreateOrderItemSearch] = useState('');
  const [createOrderSubmitting, setCreateOrderSubmitting] = useState(false);
  const [createOrderStagedServices, setCreateOrderStagedServices] = useState([]);
  const [createOrderSelectedAddons, setCreateOrderSelectedAddons] = useState({});

  // Add Service Modal State
  const [showAddServiceModal, setShowAddServiceModal] = useState(false);
  const [servicesList, setServicesList] = useState([]);
  const [selectedServiceType, setSelectedServiceType] = useState('Standard');
  const [selectedServiceCategory, setSelectedServiceCategory] = useState('');
  const [selectedGarmentCategory, setSelectedGarmentCategory] = useState('Men');
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [itemQuantities, setItemQuantities] = useState({});
  const [addedServiceItems, setAddedServiceItems] = useState([]);
  const [addedServiceAddons, setAddedServiceAddons] = useState({});
  const [addServiceSaving, setAddServiceSaving] = useState(false);

  // Addon catalogue (flat-fee extras like Packaging), shared by both modals above
  const [addonsList, setAddonsList] = useState([]);

  useEffect(() => {
    addonService.getAddons()
      .then(data => setAddonsList((data || []).filter(a => a.isActive)))
      .catch(err => console.error('Error fetching addons:', err));
  }, []);

  const toggleAddon = (setter, addonId, checked) => {
    setter(prev => {
      const next = { ...prev };
      if (checked) {
        next[addonId] = next[addonId] || 1;
      } else {
        delete next[addonId];
      }
      return next;
    });
  };

  const setAddonQty = (setter, addonId, qty) => {
    setter(prev => ({ ...prev, [addonId]: Math.max(1, parseInt(qty, 10) || 1) }));
  };

  const buildAddonsPayload = (selectedAddons) =>
    Object.entries(selectedAddons).map(([addonID, quantity]) => ({ addonID, quantity }));

  const calculateAddonsTotal = (selectedAddons) =>
    Object.entries(selectedAddons).reduce((sum, [addonID, quantity]) => {
      const addon = addonsList.find(a => a._id === addonID);
      return sum + (addon ? addon.price * quantity : 0);
    }, 0);

  const renderAddonsSection = (selectedAddons, setter) => (
    addonsList.length > 0 && (
      <div className="addons-picker">
        <div className="addons-picker-header">Addons</div>
        <div className="addons-picker-list">
          {addonsList.map(addon => {
            const checked = !!selectedAddons[addon._id];
            return (
              <div key={addon._id} className={`addons-picker-row ${checked ? 'is-selected' : ''}`}>
                <label className="addons-picker-label">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => toggleAddon(setter, addon._id, e.target.checked)}
                  />
                  <span className="addons-picker-name">{addon.name}</span>
                  <span className="addons-picker-price">₹{addon.price} each</span>
                </label>
                {checked ? (
                  <input
                    type="number"
                    className="item-qty-input"
                    min="1"
                    step="1"
                    aria-label={`${addon.name} quantity`}
                    value={selectedAddons[addon._id]}
                    onChange={(e) => setAddonQty(setter, addon._id, e.target.value)}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
        {Object.keys(selectedAddons).length > 0 && (
          <div className="addons-picker-total">
            <span>Addons</span>
            <span>₹{calculateAddonsTotal(selectedAddons).toFixed(2)}</span>
          </div>
        )}
      </div>
    )
  );

  // Edit form state
  const [editFormData, setEditFormData] = useState({
    category: '',
    service: '',
    serviceType: '',
    orderStatus: '',
    paymentStatus: '',
    orderSource: 'Call',
    assignedTo: '',
    pickupAgent: '',
    deliveryAgent: '',
    pickupDate: '',
    pickupSlotId: '',
    pickupSlot: '',
    deliveryDate: '',
    notes: '',
    items: [],
  });

  // Payment modal state (req #10)
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState({ method: 'Cash', transactionID: '' });
  const [pendingStatusOrderId, setPendingStatusOrderId] = useState(null);

  const [filters, setFilters] = useState({
    orderStatus: 'all',
    paymentStatus: 'all',
    serviceType: [],
    orderSource: [],
    category: [],
    startDate: '',
    endDate: '',
  });

  // Transform backend order to UI format
  const transformOrder = (order) => {
    const customer = order.customerID || {};
    const pickupDate = order.pickupDate ? new Date(order.pickupDate).toISOString().split('T')[0] : '';
    const deliveryDate = order.deliveryDate ? new Date(order.deliveryDate).toISOString().split('T')[0] : '';
    const currentStatus = order.orderStatus || 'New';

    // Compute live status based on dates
    const liveStatus = computeLiveStatus(currentStatus, pickupDate, deliveryDate);

    // Aggregate categories/service types across all items (multi-service orders)
    const categoryList = [...new Set(
      (order.items || [])
        .map(i => i?.serviceCategoryID?.categoryName)
        .filter(Boolean)
    )];
    const serviceTypeList = [...new Set(
      (order.items || []).map(i => i?.serviceType).filter(Boolean)
    )];

    return {
      id: order.orderId,
      _id: order._id,
      customerId: customer._id,
      customerName: customer.customerName || 'Unknown',
      customerPhone: customer.phone || '',
      society: order.deliverySociety || '',
      address: order.deliveryAddress || '',
      pincode: order.deliveryPincode || '',
      category: categoryList[0] || 'N/A',
      categoryList,
      service: categoryList.join(', ') || 'N/A',
      serviceType: serviceTypeList[0] || 'Standard',
      serviceTypeList,
      items: order.items?.flatMap(item => {
        const categoryName = item.serviceCategoryID?.categoryName || '';
        // For PerItem services with subItems
        if (item.subItems && item.subItems.length > 0) {
          return item.subItems.map(sub => ({
            name: sub.itemName,
            garmentCode: sub.garmentCode,
            qty: sub.quantity,
            price: sub.pricePerItem,
            category: categoryName,
            serviceType: item.serviceType,
          }));
        }
        // For PerKg services
        if (item.weightInKg && item.weightInKg > 0) {
          return [{
            name: `${categoryName || 'Service'} (${item.serviceType})`,
            qty: `${item.weightInKg} kg`,
            price: item.pricePerKg || item.itemTotal,
            isWeight: true,
            itemTotal: item.itemTotal,
            category: categoryName,
            serviceType: item.serviceType,
          }];
        }
        // For PerPair services
        if (item.pairCount && item.pairCount > 0) {
          return [{
            name: `${categoryName || 'Service'} (${item.serviceType})`,
            qty: `${item.pairCount} pairs`,
            price: item.pricePerPair || item.itemTotal,
            isPair: true,
            itemTotal: item.itemTotal,
            category: categoryName,
            serviceType: item.serviceType,
          }];
        }
        // Fallback
        return [{
          name: categoryName || 'Service',
          qty: 1,
          price: item.itemTotal,
          category: categoryName,
          serviceType: item.serviceType,
        }];
      }) || [],
      // Flat-fee extras (Packaging etc.) — kept separate from garment items
      addons: (order.addons || []).map(a => ({
        id: a.addonID?._id || a.addonID,
        name: a.name,
        qty: a.quantity,
        price: a.price,
        itemTotal: a.addonTotal,
        isAddon: true,
      })),
      addonsTotal: order.addonsTotal || 0,
      subtotal: order.subtotal || 0,
      discount: order.discountAmount || 0,
      discountPercentage: order.discountPercentage || 0,
      discountType: order.discountPercentage > 0 ? 'percent' : 'flat',
      discountCode: '',
      amount: order.billAmount || 0,
      orderStatus: liveStatus, // Use computed live status
      originalStatus: currentStatus, // Keep original for comparison
      statusNeedsSync: liveStatus !== currentStatus, // Flag if needs backend sync
      paymentStatus: order.paymentStatus || 'Pending',
      paymentMethod: order.paymentMethod || '',
      assignedStatus: order.pickupAgent || order.deliveryAgent ? 'Assigned' : 'Unassigned',
      assignedTo: order.pickupAgentName || order.deliveryAgentName || null,
      pickupAgent: order.pickupAgent,
      deliveryAgent: order.deliveryAgent,
      pickupDate: pickupDate,
      pickupSlot: order.pickupSlot?.slotName || '',
      pickupSlotId: order.pickupSlot?._id || order.pickupSlot,
      deliveryDate: deliveryDate,
      expectedDeliveryDate: order.expectedDeliveryDate ? new Date(order.expectedDeliveryDate).toISOString().split('T')[0] : '',
      deliverySlot: order.deliverySlot?.slotName || '',
      deliverySlotId: order.deliverySlot?._id || order.deliverySlot,
      orderSource: order.orderSource || 'Call',
      notes: '',
      createdAt: order.orderedOn ? new Date(order.orderedOn).toISOString().split('T')[0] : '',
    };
  };

  // Fetch orders from API
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        orderStatus: filters.orderStatus !== 'all' ? filters.orderStatus : undefined,
        paymentStatus: filters.paymentStatus !== 'all' ? filters.paymentStatus : undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        service: filters.serviceType.length === 1 ? filters.serviceType[0] : undefined,
        source: filters.orderSource.length === 1 ? filters.orderSource[0] : undefined,
        categories: filters.category.length > 0 ? filters.category.join(',') : undefined,
        search: debouncedSearchQuery || undefined,
      };

      const response = await orderService.getOrders(params);

      if (response && response.orders) {
        const transformedOrders = response.orders.map(transformOrder);
        setOrders(transformedOrders);
        const total = response.total || 0;
        setOrdersTotalCount(total);
        setTotalPages(Math.max(1, Math.ceil(total / ITEMS_PER_PAGE)));
        setServerActiveTotal(response.activeTotal || 0);
        setServerOrderBookRevenue(response.orderBookRevenue || 0);
        setServerOrderBookDiscount(response.orderBookDiscount || 0);
      } else {
        setOrders([]);
        setOrdersTotalCount(0);
        setTotalPages(1);
        setServerActiveTotal(0);
        setServerOrderBookRevenue(0);
        setServerOrderBookDiscount(0);
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders. Please try again.');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters.orderStatus, filters.paymentStatus, filters.startDate, filters.endDate, filters.serviceType, filters.orderSource, filters.category, debouncedSearchQuery]);

  // Fetch staff members
  const fetchStaff = useCallback(async () => {
    try {
      const data = await adminService.getAgents();
      setStaffList(data || []);
    } catch (err) {
      console.error('Error fetching staff:', err);
      setStaffList([]);
    }
  }, []);

  // Fetch pickup slots (keep {_id, slotName} — we bind selects to _id so
  // updates send an ObjectId, not a string slot name).
  const fetchSlots = useCallback(async () => {
    try {
      const data = await slotService.getSlots();
      // Both representations are consumed downstream:
      //   pickupSlotsList     — {_id, slotName} pairs for <select> bound to _id
      //   pickupSlotsFullList — full slot objects (used for create-order flow)
      const pickupSlots = (data || []).filter(slot => slot.slotType === 'Pickup');
      setPickupSlotsFullList(pickupSlots);
      setPickupSlotsList(pickupSlots.map(slot => ({ _id: slot._id, slotName: slot.slotName })));
    } catch (err) {
      console.error('Error fetching slots:', err);
      setPickupSlotsList([]);
      setPickupSlotsFullList([]);
    }
  }, []);

  // Fetch services for Add Service modal
  const fetchServices = useCallback(async () => {
    try {
      const data = await serviceService.getServices();
      setServicesList(data || []);
      // Auto-select first service if available
      if (data && data.length > 0) {
        setSelectedServiceCategory(data[0]._id);
      }
    } catch (err) {
      console.error('Error fetching services:', err);
      setServicesList([]);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchStaff();
    fetchSlots();
    fetchServices();
  }, [fetchOrders, fetchStaff, fetchSlots, fetchServices]);

  // Auto-sync status changes to backend when orders are loaded.
  // Runs in parallel and applies a single batched setOrders at the end,
  // so N syncs produce one render, not N.
  useEffect(() => {
    const toSync = orders.filter(o => o.statusNeedsSync);
    if (toSync.length === 0) return;

    let cancelled = false;
    (async () => {
      const results = await Promise.allSettled(
        toSync.map(o => orderService.updateOrderStatus(o._id, o.orderStatus))
      );
      if (cancelled) return;
      const syncedIds = new Set(
        toSync
          .filter((_, i) => results[i].status === 'fulfilled')
          .map(o => o.id)
      );
      if (syncedIds.size === 0) return;
      setOrders(prev =>
        prev.map(o =>
          syncedIds.has(o.id)
            ? { ...o, statusNeedsSync: false, originalStatus: o.orderStatus }
            : o
        )
      );
    })();

    return () => { cancelled = true; };
  }, [orders]);

  // Refresh orders periodically to keep status updated (every 5 minutes).
  // Skip while any modal is open so we don't wipe the user's in-progress edit.
  useEffect(() => {
    const intervalId = setInterval(() => {
      const anyModalOpen =
        showDetailsModal || showEditModal || showRescheduleModal ||
        showDiscountModal || showAddServiceModal || showFilterModal;
      if (!anyModalOpen) fetchOrders();
    }, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [fetchOrders, showDetailsModal, showEditModal, showRescheduleModal, showDiscountModal, showAddServiceModal, showFilterModal]);

  // Backend handles all filters, including search (order ID, customer name/phone) —
  // it must, since search has to reach across every page, not just the loaded one.
  const filteredOrders = orders;

  const getStatusBadgeClass = (status) => {
    const classes = {
      'New': 'badge-primary',
      'Cancelled': 'badge-danger',
      'Pick-up Pending': 'badge-warning',
      'In-Progress': 'badge-info',
      'Delivery Pending': 'badge-warning',
      'Delivered': 'badge-success',
      'Pending': 'badge-warning',
      'Confirmed': 'badge-success',
      'Assigned': 'badge-success',
      'Unassigned': 'badge-danger',
    };
    return classes[status] || 'badge-neutral';
  };

  const getCategoryBadgeClass = (category) => {
    const classes = {
      'Washing': 'badge-primary',
      'Dry Clean': 'badge-info',
      'Ironing': 'badge-warning',
      'Combo': 'badge-success',
      'Add-on': 'badge-neutral',
    };
    return classes[category] || 'badge-neutral';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'New': <Clock size={16} />,
      'Cancelled': <XCircle size={16} />,
      'Pick-up Pending': <Truck size={16} />,
      'In-Progress': <Clock size={16} />,
      'Delivery Pending': <Truck size={16} />,
      'Delivered': <CheckCircle size={16} />,
    };
    return icons[status] || null;
  };

  const getSourceBadge = (source) => {
    const map = {
      'WhatsApp':    { icon: <WhatsappLogo size={12} />, cls: 'badge-source-whatsapp' },
      'App':         { icon: <DeviceMobile size={12} />, cls: 'badge-source-app' },
      'Call':        { icon: <Phone size={12} />,        cls: 'badge-source-call' },
      'Walk-in':     { icon: <Storefront size={12} />,   cls: 'badge-source-store' },
      'Store Visit': { icon: <Storefront size={12} />,   cls: 'badge-source-store' },
    };
    const s = map[source] || { icon: null, cls: 'badge-neutral' };
    return (
      <span className={`badge badge-source ${s.cls}`}>
        {s.icon}{source || 'Unknown'}
      </span>
    );
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const calculateSubtotal = (items) => {
    return items.reduce((sum, item) => {
      // For weight/pair items, use itemTotal directly
      if (item.itemTotal) {
        return sum + item.itemTotal;
      }
      // For regular items, multiply price by quantity
      const qty = typeof item.qty === 'number' ? item.qty : 1;
      return sum + (item.price * qty);
    }, 0);
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  const handleEditOrder = (order) => {
    setSelectedOrder(order);
    setEditFormData({
      category: order.category,
      service: order.service,
      serviceType: order.serviceType,
      orderStatus: order.orderStatus,
      paymentStatus: order.paymentStatus,
      orderSource: order.orderSource || 'Call',
      assignedTo: order.assignedTo || '',
      pickupAgent: order.pickupAgent || '',
      deliveryAgent: order.deliveryAgent || '',
      pickupDate: order.pickupDate,
      pickupSlotId: order.pickupSlotId || '',
      pickupSlot: order.pickupSlot,
      deliveryDate: order.deliveryDate,
      notes: order.notes || '',
      items: [...order.items],
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    // Req #9: Validate staff assignment before saving pickup/delivery status
    const isPickupStatus = editFormData.orderStatus === 'Pick-up Pending' || editFormData.orderStatus === 'In-Progress';
    const isDeliveryStatus = editFormData.orderStatus === 'Delivery Pending' || editFormData.orderStatus === 'Delivered';

    if (isPickupStatus && !editFormData.pickupAgent) {
      alert('Executive not assigned. Please assign a Pickup Executive before saving.');
      return;
    }
    if (isDeliveryStatus && !editFormData.deliveryAgent) {
      alert('Executive not assigned. Please assign a Delivery Executive before saving.');
      return;
    }

    // Req #10: Payment mandatory when marking as Delivered
    if (editFormData.orderStatus === 'Delivered' && editFormData.paymentStatus !== 'Confirmed') {
      setPendingStatusOrderId({ orderId: selectedOrder.id, fromEdit: true });
      setPaymentFormData({ method: 'Cash', transactionID: '' });
      setShowPaymentModal(true);
      return;
    }

    try {
      const updateData = {
        orderStatus: editFormData.orderStatus,
        paymentStatus: editFormData.paymentStatus,
        orderSource: editFormData.orderSource,
      };
      if (editFormData.pickupDate) updateData.pickupDate = editFormData.pickupDate;
      if (editFormData.pickupSlotId) updateData.pickupSlot = editFormData.pickupSlotId;
      if (editFormData.deliveryDate) updateData.deliveryDate = editFormData.deliveryDate;
      if (editFormData.pickupAgent) updateData.pickupAgent = editFormData.pickupAgent;
      if (editFormData.deliveryAgent) updateData.deliveryAgent = editFormData.deliveryAgent;

      // Map assignedTo (staff name) to a pickupAgent id so the backend
      // can persist the assignment. Empty string clears the agent.
      if (editFormData.assignedTo !== undefined) {
        if (editFormData.assignedTo) {
          const agent = staffList.find(s => s.name === editFormData.assignedTo);
          if (agent?._id) updateData.pickupAgent = agent._id;
        } else {
          updateData.pickupAgent = null;
        }
      }

      const updatedOrder = await orderService.updateOrder(selectedOrder.id, updateData);
      const transformed = transformOrder(updatedOrder);
      setOrders((prev) =>
        prev.map((order) =>
          order.id === selectedOrder.id ? transformed : order
        )
      );

      setShowEditModal(false);
      setSelectedOrder(null);
    } catch (err) {
      console.error('Error updating order:', err);
      alert('Failed to update order');
    }
  };

  const handleReschedule = async (order) => {
    setSelectedOrder(order);
    setRescheduleData({
      pickupDate: order.pickupDate,
      pickupSlot: order.pickupSlotId || '',
      deliveryDate: order.deliveryDate,
    });
    setRescheduleSlots([]);
    setShowRescheduleModal(true);
    // Pre-load slots if pickupDate exists
    if (order.pickupDate) {
      try {
        const slots = await slotService.getSlotStatus(order.pickupDate);
        setRescheduleSlots((slots || []).filter(s => s.slotType === 'Pickup' && s.isActive !== false));
      } catch { setRescheduleSlots([]); }
    }
  };

  const handleApplyDiscount = (order) => {
    setSelectedOrder(order);
    setDiscountData({
      type: order.discountType || 'percent',
      value: order.discountType === 'percent' ? '' : order.discount || '',
      code: order.discountCode || '',
    });
    setShowDiscountModal(true);
  };

  const handleSaveDiscount = async () => {
    const discountValue = parseFloat(discountData.value) || 0;
    try {
      const payload = discountData.type === 'percent'
        ? { discountPercentage: discountValue }
        : { discountAmount: discountValue };

      const updatedOrder = await orderService.updateOrder(selectedOrder.id, payload);
      const transformed = transformOrder(updatedOrder);
      setOrders((prev) =>
        prev.map((order) =>
          order.id === selectedOrder.id ? transformed : order
        )
      );
      setShowDiscountModal(false);
      setSelectedOrder(null);
    } catch (err) {
      console.error('Error applying discount:', err);
      alert('Failed to apply discount');
    }
  };

  const handleSaveReschedule = async () => {
    try {
      const updateData = {};
      if (rescheduleData.pickupDate) updateData.pickupDate = rescheduleData.pickupDate;
      if (rescheduleData.pickupSlot) updateData.pickupSlot = rescheduleData.pickupSlot;
      if (rescheduleData.deliveryDate) updateData.deliveryDate = rescheduleData.deliveryDate;

      const updatedOrder = await orderService.updateOrder(selectedOrder.id, updateData);
      const transformed = transformOrder(updatedOrder);
      setOrders((prev) =>
        prev.map((order) =>
          order.id === selectedOrder.id ? transformed : order
        )
      );
      setShowRescheduleModal(false);
      setSelectedOrder(null);
    } catch (err) {
      console.error('Error rescheduling order:', err);
      alert('Failed to reschedule order');
    }
  };

  // Create Order Modal Handlers
  const handleOpenCreateOrder = async () => {
    setCreatePhone('');
    setCreateCustomer(null);
    setCreatePhoneError('');
    setIsNewCustomer(false);
    setCreateNewCustomerName('');
    setCreateOrderItemQtys({});
    setCreateOrderStagedServices([]);
    setCreateOrderSelectedAddons({});
    setCreateOrderGarmentCat('Men');
    setCreateOrderItemSearch('');
    setCreateOrderFormData({
      orderSource: 'Call', isPickupScheduled: true, pickupDate: '', pickupSlot: '',
      deliveryDate: '', serviceCategory: '', serviceType: 'Standard', deliveryType: 'Home Delivery',
      selectedAddressIdx: 0
    });
    setShowCreateOrderModal(true);
    if (createOrderServices.length === 0) {
      try {
        const data = await serviceService.getServices();
        setCreateOrderServices(data || []);
        if (data && data.length > 0) {
          setCreateOrderFormData(prev => ({ ...prev, serviceCategory: data[0]._id }));
          const firstCat = data[0]?.subcategories?.[0]?.garmentCategory;
          setCreateOrderGarmentCat(firstCat || 'Men');
        }
      } catch (err) {
        console.error('Error fetching services:', err);
      }
    } else {
      setCreateOrderFormData(prev => ({ ...prev, serviceCategory: createOrderServices[0]._id }));
      const firstCat = createOrderServices[0]?.subcategories?.[0]?.garmentCategory;
      setCreateOrderGarmentCat(firstCat || 'Men');
    }
  };

  const handleCreatePhoneChange = async (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 10);
    setCreatePhone(digits);
    setCreatePhoneError('');
    setIsNewCustomer(false);
    setCreateNewCustomerName('');
    setCreateCustomer(null);
    if (digits.length === 10) {
      setCreatePhoneLookupLoading(true);
      try {
        const data = await customerService.checkByPhone(digits);
        const customer = data?.customer || (data?._id ? data : null);
        if (customer) {
          setCreateCustomer(customer);
          setIsNewCustomer(false);
          setCreateOrderFormData(prev => ({ ...prev, selectedAddressIdx: 0 }));
        } else {
          setIsNewCustomer(true);
        }
      } catch (err) {
        setIsNewCustomer(true);
      } finally {
        setCreatePhoneLookupLoading(false);
      }
    } else {
      setCreateCustomer(null);
    }
  };

  const getDeliveryDaysForType = (serviceType) => {
    if (serviceType === 'Express') return 1;
    if (serviceType === 'Semi Express') return 2;
    return 3;
  };

  // Multi-service orders use the slowest TAT among staged (+ current) types.
  const getMaxDeliveryDays = (extraType) => {
    const types = [
      ...createOrderStagedServices.map(e => e.serviceType),
      ...(extraType ? [extraType] : []),
    ];
    if (types.length === 0) return getDeliveryDaysForType(createOrderFormData.serviceType);
    return Math.max(...types.map(getDeliveryDaysForType));
  };

  const computeDeliveryDateFromPickup = (pickupDate, days) => {
    if (!pickupDate) return '';
    const d = new Date(pickupDate);
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  };

  const handleCreateOrderPickupDate = async (date) => {
    const deliveryDate = computeDeliveryDateFromPickup(date, getMaxDeliveryDays());
    setCreateOrderFormData(prev => ({ ...prev, pickupDate: date, pickupSlot: '', deliveryDate }));
    if (date) {
      try {
        const slots = await slotService.getAvailablePickupSlots(date);
        setCreateOrderSlots(slots || []);
      } catch { setCreateOrderSlots([]); }
    } else {
      setCreateOrderSlots([]);
    }
  };

  const handleCreateServiceTypeChange = (serviceType) => {
    const deliveryDate = computeDeliveryDateFromPickup(
      createOrderFormData.pickupDate,
      getMaxDeliveryDays(serviceType)
    );
    setCreateOrderFormData(prev => ({ ...prev, serviceType, deliveryDate }));
    setCreateOrderItemQtys({});
  };

  const getCreateSelectedService = () => createOrderServices.find(s => s._id === createOrderFormData.serviceCategory);

  const getCreatePriceMultiplier = () => {
    const s = getCreateSelectedService();
    if (createOrderFormData.serviceType === 'Semi Express') return s?.semiExpressPriceMultiplier || 1.5;
    if (createOrderFormData.serviceType === 'Express') return s?.expressPriceMultiplier || 2;
    return 1;
  };

  const getCreateServiceGarmentCategories = () => {
    const s = getCreateSelectedService();
    if (!s || !s.subcategories || s.subcategories.length === 0) return garmentCategories;
    const cats = [...new Set(s.subcategories.map(sc => sc.garmentCategory).filter(Boolean))];
    return cats.length > 0 ? cats : garmentCategories;
  };

  const getCreateFilteredSubcategories = () => {
    const s = getCreateSelectedService();
    if (!s?.subcategories) return [];
    return s.subcategories.filter(item => {
      const matchesCat = item.garmentCategory === createOrderGarmentCat;
      const matchesSearch = !createOrderItemSearch ||
        item.itemName.toLowerCase().includes(createOrderItemSearch.toLowerCase()) ||
        (item.garmentCode && item.garmentCode.toLowerCase().includes(createOrderItemSearch.toLowerCase()));
      return matchesCat && matchesSearch;
    });
  };

  const handleCreateOrderSubmit = async () => {
    if (!createCustomer && !isNewCustomer) { alert('Please enter a valid 10-digit phone number.'); return; }
    if (isNewCustomer && !createNewCustomerName.trim()) { alert('Please enter the customer name.'); return; }

    // Create new customer inline if needed
    let activeCustomer = createCustomer;
    if (isNewCustomer) {
      try {
        const res = await customerService.createCustomer({ customerName: createNewCustomerName.trim(), phone: createPhone });
        activeCustomer = res.customer || res;
        setCreateCustomer(activeCustomer);
        setIsNewCustomer(false);
      } catch (err) {
        alert('Failed to create customer: ' + (err.response?.data?.message || err.message));
        return;
      }
    }
    if (createOrderStagedServices.length === 0) { alert('Please add at least one service'); return; }
    if (createOrderFormData.isPickupScheduled && (!createOrderFormData.pickupDate || !createOrderFormData.pickupSlot)) {
      alert('Please select pickup date and slot'); return;
    }

    const items = createOrderStagedServices.flatMap(entry => entry.items);
    const subtotal = items.reduce((sum, item) => sum + item.itemTotal, 0);
    const addonsTotal = calculateAddonsTotal(createOrderSelectedAddons);
    // Ensure delivery date reflects slowest TAT across all staged services
    const deliveryDate = createOrderFormData.pickupDate
      ? computeDeliveryDateFromPickup(createOrderFormData.pickupDate, getMaxDeliveryDays())
      : (createOrderFormData.deliveryDate || undefined);
    setCreateOrderSubmitting(true);
    try {
      await orderService.createOrder({
        customerID: activeCustomer._id || activeCustomer.id,
        orderSource: createOrderFormData.orderSource,
        isPickupScheduled: createOrderFormData.isPickupScheduled,
        serviceType: createOrderFormData.serviceType,
        pickupDate: createOrderFormData.pickupDate || undefined,
        pickupSlot: createOrderFormData.pickupSlot || undefined,
        deliveryDate: deliveryDate || undefined,
        expectedDeliveryDate: deliveryDate || undefined,
        deliveryType: createOrderFormData.deliveryType,
        discountPercentage: 0,
        subtotal,
        finalAmount: subtotal + addonsTotal,
        items,
        addons: buildAddonsPayload(createOrderSelectedAddons)
      });
      setShowCreateOrderModal(false);
      setCreateOrderStagedServices([]);
      setCreateOrderSelectedAddons({});
      fetchOrders();
    } catch (err) {
      console.error('Error creating order:', err);
      alert(err.response?.data?.message || 'Failed to create order');
    } finally {
      setCreateOrderSubmitting(false);
    }
  };

  const handleStageCurrentService = () => {
    const service = getCreateSelectedService();
    if (!service) { alert('Please select a service'); return; }

    const serviceType = createOrderFormData.serviceType;
    const multiplier = getCreatePriceMultiplier();
    let items = [];

    if (service.pricingModel === 'PerKg') {
      const weight = parseFloat(createOrderItemQtys['weight']) || 0;
      if (weight <= 0) { alert('Please enter the weight'); return; }
      const pricePerKg = service.pricePerKg * multiplier;
      items = [{
        serviceCategoryID: service._id,
        serviceCategoryName: service.categoryName,
        serviceType,
        pricingModel: 'PerKg',
        itemName: service.categoryName,
        weightInKg: weight,
        pricePerKg,
        quantity: weight,
        itemTotal: weight * pricePerKg,
      }];
    } else if (service.pricingModel === 'PerItem') {
      items = Object.entries(createOrderItemQtys)
        .filter(([key, qty]) => qty > 0 && key !== 'weight' && key !== 'pairs')
        .map(([key, qty]) => {
          const sub = service.subcategories.find(s => s.garmentCode === key || s.itemName === key);
          if (!sub) return null;
          return {
            serviceCategoryID: service._id,
            serviceCategoryName: service.categoryName,
            serviceType,
            pricingModel: 'PerItem',
            itemName: sub.itemName,
            garmentCode: sub.garmentCode || '',
            garmentCategory: sub.garmentCategory,
            quantity: qty,
            pricePerItem: sub.price * multiplier,
            itemTotal: qty * sub.price * multiplier,
          };
        }).filter(Boolean);
      if (items.length === 0) { alert('Please add at least one item'); return; }
    } else if (service.pricingModel === 'PerPair') {
      const pairs = parseFloat(createOrderItemQtys['pairs']) || 0;
      if (pairs <= 0) { alert('Please enter the number of pairs'); return; }
      const pricePerPair = service.pricePerPair * multiplier;
      items = [{
        serviceCategoryID: service._id,
        serviceCategoryName: service.categoryName,
        serviceType,
        pricingModel: 'PerPair',
        itemName: service.categoryName,
        pairCount: pairs,
        pricePerPair,
        quantity: pairs,
        itemTotal: pairs * pricePerPair,
      }];
    }

    const subtotal = items.reduce((sum, item) => sum + item.itemTotal, 0);
    setCreateOrderStagedServices(prev => {
      const next = [...prev, {
        serviceId: service._id,
        serviceName: service.categoryName,
        serviceType,
        pricingModel: service.pricingModel,
        items,
        subtotal,
      }];
      // Refresh est. delivery from slowest TAT across all staged services
      if (createOrderFormData.pickupDate) {
        const days = Math.max(...next.map(e => getDeliveryDaysForType(e.serviceType)));
        const deliveryDate = computeDeliveryDateFromPickup(createOrderFormData.pickupDate, days);
        setCreateOrderFormData(fd => ({ ...fd, deliveryDate }));
      }
      return next;
    });
    setCreateOrderItemQtys({});
  };

  const handleRemoveStagedService = (index) => {
    setCreateOrderStagedServices(prev => {
      const next = prev.filter((_, i) => i !== index);
      if (createOrderFormData.pickupDate) {
        const days = next.length
          ? Math.max(...next.map(e => getDeliveryDaysForType(e.serviceType)))
          : getDeliveryDaysForType(createOrderFormData.serviceType);
        const deliveryDate = computeDeliveryDateFromPickup(createOrderFormData.pickupDate, days);
        setCreateOrderFormData(fd => ({ ...fd, deliveryDate }));
      }
      return next;
    });
  };

  // Add Service Modal Handlers
  const handleOpenAddService = (order) => {
    setSelectedOrder(order);
    setShowAddServiceModal(true);
    setSelectedServiceType('Standard');
    setItemSearchQuery('');
    setItemQuantities({});
    setAddedServiceItems([]);
    setAddedServiceAddons({});
    // Set first service as default if available — also seed garment tab
    // to the first real category for that service (avoids stale 'Men' tab
    // when the useEffect doesn't re-fire for unchanged service id).
    if (servicesList.length > 0) {
      const firstService = servicesList[0];
      setSelectedServiceCategory(firstService._id);
      const firstCat = firstService.subcategories?.[0]?.garmentCategory;
      setSelectedGarmentCategory(firstCat || 'Men');
    } else {
      setSelectedGarmentCategory('Men');
    }
  };

  const handleCloseAddService = () => {
    setShowAddServiceModal(false);
    setSelectedOrder(null);
    setItemQuantities({});
    setAddedServiceItems([]);
    setAddedServiceAddons({});
  };

  // Get selected service object
  const getSelectedService = () => {
    return servicesList.find(s => s._id === selectedServiceCategory);
  };

  // Get price multiplier based on service type
  const getPriceMultiplier = (service, serviceType = selectedServiceType) => {
    if (serviceType === 'Semi Express') {
      return service?.semiExpressPriceMultiplier ?? 1.5;
    } else if (serviceType === 'Express') {
      return service?.expressPriceMultiplier ?? 2;
    }
    return 1;
  };

  // Get unique garment categories present in the selected service
  const getServiceGarmentCategories = () => {
    const service = getSelectedService();
    if (!service || !service.subcategories || service.subcategories.length === 0) return garmentCategories;
    const cats = [...new Set(service.subcategories.map(s => s.garmentCategory).filter(Boolean))];
    return cats.length > 0 ? cats : garmentCategories;
  };

  // Get filtered subcategories based on garment category and search
  const getFilteredSubcategories = () => {
    const service = getSelectedService();
    if (!service || !service.subcategories) return [];

    return service.subcategories.filter(item => {
      const matchesCategory = item.garmentCategory === selectedGarmentCategory;
      const matchesSearch = !itemSearchQuery ||
        item.itemName.toLowerCase().includes(itemSearchQuery.toLowerCase()) ||
        (item.garmentCode && item.garmentCode.toLowerCase().includes(itemSearchQuery.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
  };

  // Reset garment tab and quantities when service category changes
  useEffect(() => {
    if (!selectedServiceCategory) return;
    setItemQuantities({});
    setItemSearchQuery('');
    const service = servicesList.find(s => s._id === selectedServiceCategory);
    if (service?.subcategories?.length > 0) {
      setSelectedGarmentCategory(service.subcategories[0].garmentCategory);
    } else {
      setSelectedGarmentCategory('Men');
    }
  }, [selectedServiceCategory, servicesList]);

  // Handle quantity change for an item
  const handleQuantityChange = (itemName, quantity) => {
    setItemQuantities(prev => ({
      ...prev,
      [itemName]: Math.max(0, parseFloat(quantity) || 0)
    }));
  };

  // Build staged line items from current quantity inputs (does not mutate state).
  // Used by Add Item(s) and by Save so PerKg/PerPair work without a prior click.
  const buildItemsFromQuantities = (service, quantities, serviceType) => {
    if (!service) return [];
    const multiplier = getPriceMultiplier(service, serviceType);
    const newItems = [];

    if (service.pricingModel === 'PerKg') {
      const weight = parseFloat(quantities.weight);
      if (weight > 0) {
        const pricePerKg = (service.pricePerKg || 0) * multiplier;
        newItems.push({
          serviceCategoryId: service._id,
          serviceCategoryName: service.categoryName,
          serviceType,
          pricingModel: 'PerKg',
          itemName: service.categoryName,
          weightInKg: weight,
          quantity: weight,
          pricePerKg,
          itemTotal: weight * pricePerKg,
        });
      }
    } else if (service.pricingModel === 'PerPair') {
      const pairs = parseInt(quantities.pairs, 10);
      if (pairs > 0) {
        const pricePerPair = (service.pricePerPair || 0) * multiplier;
        newItems.push({
          serviceCategoryId: service._id,
          serviceCategoryName: service.categoryName,
          serviceType,
          pricingModel: 'PerPair',
          itemName: service.categoryName,
          pairCount: pairs,
          quantity: pairs,
          pricePerPair,
          itemTotal: pairs * pricePerPair,
        });
      }
    } else {
      // PerItem — keys are garmentCode (preferred) or itemName fallback.
      Object.entries(quantities).forEach(([key, qty]) => {
        if (!(qty > 0) || key === 'weight' || key === 'pairs') return;
        const subcategory = service.subcategories?.find(
          s => s.garmentCode === key || s.itemName === key
        );
        if (!subcategory) return;
        newItems.push({
          serviceCategoryId: service._id,
          serviceCategoryName: service.categoryName,
          serviceType,
          pricingModel: 'PerItem',
          itemName: subcategory.itemName,
          garmentCode: subcategory.garmentCode,
          quantity: qty,
          pricePerItem: subcategory.price * multiplier,
          itemTotal: qty * subcategory.price * multiplier,
        });
      });
    }
    return newItems;
  };

  // Add items to the staged list. Handles all three pricing models.
  const handleAddItems = () => {
    const service = getSelectedService();
    const newItems = buildItemsFromQuantities(service, itemQuantities, selectedServiceType);
    if (newItems.length > 0) {
      setAddedServiceItems(prev => [...prev, ...newItems]);
      setItemQuantities({});
    }
  };

  const hasPendingQuantities = () => {
    const service = getSelectedService();
    if (!service) return false;
    return buildItemsFromQuantities(service, itemQuantities, selectedServiceType).length > 0;
  };

  // Remove added service item
  const handleRemoveServiceItem = (index) => {
    setAddedServiceItems(prev => prev.filter((_, i) => i !== index));
  };

  // Calculate total for added items (staged + still-pending form qty)
  const calculateAddedItemsTotal = () => {
    const pending = buildItemsFromQuantities(
      getSelectedService(),
      itemQuantities,
      selectedServiceType
    );
    return [...addedServiceItems, ...pending].reduce((sum, item) => sum + item.itemTotal, 0);
  };

  const calculateAddServiceGrandTotal = () =>
    calculateAddedItemsTotal() + calculateAddonsTotal(addedServiceAddons);

  // Save added services to order
  const handleSaveAddedServices = async () => {
    // Flush any quantities still in the form so PerKg/PerPair (and PerItem)
    // save even if the user never clicked "Add Item(s)".
    const pendingItems = buildItemsFromQuantities(
      getSelectedService(),
      itemQuantities,
      selectedServiceType
    );
    const itemsToSave = [...addedServiceItems, ...pendingItems];
    if (itemsToSave.length === 0 && Object.keys(addedServiceAddons).length === 0) return;
    if (!selectedOrder?.id) {
      alert('No order selected. Close and open Add Service again.');
      return;
    }

    setAddServiceSaving(true);
    try {
      // Fetch fresh order from backend to get items in proper format
      const fullOrder = await orderService.getOrder(selectedOrder.id);

      // Build new items in backend format.
      // PerItem: group by service+type so subItems collapse into one entry.
      // PerKg/PerPair: each added entry is its own item (weight/pairs are
      // numeric, not groupable into subItems).
      const perItemByGroup = {};
      const nonPerItem = [];
      itemsToSave.forEach(item => {
        if (item.pricingModel === 'PerKg') {
          nonPerItem.push({
            serviceCategoryID: item.serviceCategoryId,
            serviceType: item.serviceType,
            weightInKg: item.weightInKg,
            pricePerKg: item.pricePerKg,
          });
        } else if (item.pricingModel === 'PerPair') {
          nonPerItem.push({
            serviceCategoryID: item.serviceCategoryId,
            serviceType: item.serviceType,
            pairCount: item.pairCount,
            pricePerPair: item.pricePerPair,
          });
        } else {
          const key = `${item.serviceCategoryId}_${item.serviceType}`;
          if (!perItemByGroup[key]) {
            perItemByGroup[key] = {
              serviceCategoryID: item.serviceCategoryId,
              serviceType: item.serviceType,
              subItems: []
            };
          }
          perItemByGroup[key].subItems.push({
            itemName: item.itemName,
            garmentCode: item.garmentCode,
            quantity: item.quantity,
            pricePerItem: item.pricePerItem
          });
        }
      });

      // Merge with existing items. PerItem rows for the same service+type
      // fold into one entry (avoids duplicate Dry Cleaning lines).
      const mergedItems = [];
      const existingPerItemKeys = new Set();

      (fullOrder.items || []).forEach(item => {
        const serviceCategoryID = item.serviceCategoryID?._id || item.serviceCategoryID;
        const serviceType = item.serviceType;
        const key = `${serviceCategoryID}_${serviceType}`;
        const incoming = perItemByGroup[key];

        if (incoming && Array.isArray(item.subItems)) {
          existingPerItemKeys.add(key);
          mergedItems.push({
            serviceCategoryID,
            serviceType,
            subItems: [
              ...(item.subItems || []).map(s => ({
                itemName: s.itemName,
                garmentCode: s.garmentCode,
                quantity: s.quantity,
                pricePerItem: s.pricePerItem,
              })),
              ...incoming.subItems,
            ],
          });
          return;
        }

        mergedItems.push({
          serviceCategoryID,
          serviceType,
          weightInKg: item.weightInKg,
          pairCount: item.pairCount,
          pricePerKg: item.pricePerKg,
          pricePerPair: item.pricePerPair,
          subItems: item.subItems,
        });
      });

      Object.entries(perItemByGroup).forEach(([key, group]) => {
        if (!existingPerItemKeys.has(key)) mergedItems.push(group);
      });
      mergedItems.push(...nonPerItem);

      // Merge existing addons on the order with newly selected ones (same
      // addon selected again just bumps its quantity). Only send addons when
      // the user actually picked some OR the order already has them — so a
      // services-only save doesn't wipe existing addons (empty [] would).
      let addonsPayload;
      if (Object.keys(addedServiceAddons).length > 0 || (fullOrder.addons || []).length > 0) {
        const addonsMap = {};
        (fullOrder.addons || []).forEach(a => {
          const id = String(a.addonID?._id || a.addonID);
          if (!id || id === 'undefined' || id === 'null') return;
          addonsMap[id] = (addonsMap[id] || 0) + (a.quantity || 0);
        });
        Object.entries(addedServiceAddons).forEach(([id, qty]) => {
          addonsMap[id] = (addonsMap[id] || 0) + qty;
        });
        addonsPayload = Object.entries(addonsMap).map(([addonID, quantity]) => ({ addonID, quantity }));
      }

      const updatedOrder = await orderService.updateOrderItems(selectedOrder.id, mergedItems, addonsPayload);
      const transformed = transformOrder(updatedOrder);
      setOrders(prev => prev.map(order =>
        order.id === selectedOrder.id ? transformed : order
      ));
      setSelectedOrder(transformed);

      handleCloseAddService();
    } catch (err) {
      console.error('Error adding services to order:', err);
      alert(err.response?.data?.message || 'Failed to add services to order');
    } finally {
      setAddServiceSaving(false);
    }
  };

  const handleUpdateOrderStatus = (newStatus) => {
    if (!selectedOrder?.id) return;

    // Req #10: Payment mandatory when marking as Delivered
    if (newStatus === 'Delivered' && selectedOrder.paymentStatus !== 'Confirmed') {
      setPendingStatusOrderId({ orderId: selectedOrder.id, fromEdit: false, newStatus });
      setPaymentFormData({ method: 'Cash', transactionID: '' });
      setShowPaymentModal(true);
      return;
    }

    handleUpdateOrderStatusAPI(selectedOrder.id, newStatus);
  };

  // Save payment then apply pending status (Req #10)
  const handleSavePaymentAndStatus = async () => {
    if (paymentFormData.method === 'UPI' && !paymentFormData.transactionID.trim()) {
      alert('Transaction ID is mandatory for UPI payments. Please enter the Transaction ID.');
      return;
    }

    if (!pendingStatusOrderId) return;
    const { orderId, fromEdit, newStatus } = pendingStatusOrderId;

    try {
      // Save payment
      await orderService.updatePaymentStatus(orderId, 'Confirmed', paymentFormData.method, paymentFormData.transactionID || undefined);
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, paymentStatus: 'Confirmed', paymentMethod: paymentFormData.method } : o));
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, paymentStatus: 'Confirmed', paymentMethod: paymentFormData.method }));
      }

      // Now apply Delivered status
      const targetStatus = newStatus || 'Delivered';
      await orderService.updateOrderStatus(orderId, targetStatus);
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, orderStatus: targetStatus } : o));
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, orderStatus: targetStatus }));
      }

      if (fromEdit) {
        setShowEditModal(false);
        setSelectedOrder(null);
      }

      setShowPaymentModal(false);
      setPendingStatusOrderId(null);
    } catch (err) {
      console.error('Error saving payment/status:', err);
      alert('Failed to save payment details');
    }
  };

  const handleUpdatePaymentStatus = (newStatus) => {
    if (selectedOrder.id) {
      handleUpdatePaymentStatusAPI(selectedOrder.id, newStatus);
    }
  };

  const handlePrint = async (order) => {
    if (!order) return;
    try {
      const blob = await orderService.generateInvoice(order.id);
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => printWindow.print();
      }
    } catch (err) {
      console.error('Error generating invoice:', err);
      alert('Failed to generate invoice. Please try again.');
    }
  };

  // Print small tag slips for garment tagging
  const handlePrintTags = async (order) => {
    if (!order) return;

    try {
      const blob = await orderService.generateTagSlips(order.id);
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
          // Release the blob once the preview/print dialog has it.
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        };
      } else {
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Error generating tag slips:', err);
      alert('Failed to generate tag slips. Please try again.');
    }
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    fetchOrders();
  };

  // Pagination handlers
  const goToPage = (page) => {
    const next = Math.min(totalPages, Math.max(1, page));
    if (next === currentPage) return;
    setCurrentPage(next);
    // Keep the table in view when changing pages
    requestAnimationFrame(() => {
      document.querySelector('.orders-table-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleNextPage = () => goToPage(currentPage + 1);
  const handlePrevPage = () => goToPage(currentPage - 1);

  // Compact window: first, last, and neighbors around current (±1)
  const getPaginationItems = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
    return [...pages]
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b)
      .reduce((acc, p, idx, arr) => {
        if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
        acc.push(p);
        return acc;
      }, []);
  };

  // Update order status via API
  const handleUpdateOrderStatusAPI = async (orderId, newStatus) => {
    try {
      await orderService.updateOrderStatus(orderId, newStatus);
      setOrders((prev) =>
        prev.map((order) =>
          order.id === orderId ? { ...order, orderStatus: newStatus } : order
        )
      );
      if (selectedOrder?.id === orderId) {
        setSelectedOrder((prev) => ({ ...prev, orderStatus: newStatus }));
      }
    } catch (err) {
      console.error('Error updating order status:', err);
      alert('Failed to update order status');
    }
  };

  // Explicit "Cancel Order" action from details modal footer.
  // The Quick Status Update dropdown also cancels (auto-saves on change),
  // but lacked a confirm step and visible feedback — users assumed nothing
  // happened. This path adds a confirmation, refetches the list, and closes
  // the modal so the cancelled state is unambiguously reflected.
  const handleCancelOrder = async () => {
    if (!selectedOrder?.id) return;
    if (selectedOrder.orderStatus === 'Cancelled' || selectedOrder.orderStatus === 'Delivered') return;
    const ok = window.confirm(`Cancel order ${selectedOrder.id}? This cannot be undone.`);
    if (!ok) return;
    try {
      await orderService.updateOrderStatus(selectedOrder.id, 'Cancelled');
      setOrders((prev) => prev.map((o) => o.id === selectedOrder.id ? { ...o, orderStatus: 'Cancelled' } : o));
      setShowDetailsModal(false);
      setSelectedOrder(null);
      fetchOrders();
    } catch (err) {
      console.error('Error cancelling order:', err);
      alert('Failed to cancel order. Please try again.');
    }
  };

  // Update payment status via API
  const handleUpdatePaymentStatusAPI = async (orderId, newStatus) => {
    try {
      await orderService.updatePaymentStatus(orderId, newStatus);
      setOrders((prev) =>
        prev.map((order) =>
          order.id === orderId ? { ...order, paymentStatus: newStatus } : order
        )
      );
      if (selectedOrder?.id === orderId) {
        setSelectedOrder((prev) => ({ ...prev, paymentStatus: newStatus }));
      }
    } catch (err) {
      console.error('Error updating payment status:', err);
      alert('Failed to update payment status');
    }
  };

  // RFC 4180 cell escape — wrap in quotes and double any embedded quotes.
  const csvCell = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  // Export pulls the full matching set from the server (not just the loaded
  // page) so a picked date range actually covers all orders in that range.
  const fetchOrdersForExport = async () => {
    const { startDate, endDate } = exportRange;
    const params = {
      page: 1,
      limit: 100000,
      orderStatus: filters.orderStatus !== 'all' ? filters.orderStatus : undefined,
      paymentStatus: filters.paymentStatus !== 'all' ? filters.paymentStatus : undefined,
      startDate: startDate || filters.startDate || undefined,
      endDate: endDate || filters.endDate || undefined,
      service: filters.serviceType.length === 1 ? filters.serviceType[0] : undefined,
      source: filters.orderSource.length === 1 ? filters.orderSource[0] : undefined,
      categories: filters.category.length > 0 ? filters.category.join(',') : undefined,
      search: debouncedSearchQuery || undefined,
    };
    const response = await orderService.getOrders(params);
    return (response?.orders || []).map(transformOrder);
  };

  const handleExport = async (format) => {
    // Open the print window synchronously (before the await) so popup
    // blockers don't kill it.
    const printWindow = format === 'pdf' ? window.open('', '_blank') : null;
    setShowExportMenu(false);
    setExportLoading(true);
    let dataToExport;
    try {
      dataToExport = await fetchOrdersForExport();
    } catch (err) {
      console.error('Error exporting orders:', err);
      alert('Failed to export orders. Please try again.');
      if (printWindow) printWindow.close();
      setExportLoading(false);
      return;
    }
    setExportLoading(false);

    if (format === 'csv') {
      const headers = ['Order ID', 'Customer', 'Phone', 'Society', 'Flat/Address', 'Category', 'Service', 'Subtotal', 'Discount', 'Discount %', 'Amount', 'Status', 'Payment Method', 'Payment Status', 'Pickup Date', 'Created'];
      const csvRows = [
        headers.map(csvCell).join(','),
        ...dataToExport.map(order => [
          order.id,
          order.customerName,
          order.customerPhone,
          order.society,
          order.address,
          order.category,
          order.service,
          order.subtotal,
          order.discount,
          order.discountPercentage,
          order.amount,
          order.orderStatus,
          order.paymentMethod,
          order.paymentStatus,
          order.pickupDate,
          order.createdAt
        ].map(csvCell).join(','))
      ];
      downloadFile(csvRows.join('\n'), 'orders-export.csv', 'text/csv');
    } else if (format === 'json') {
      downloadFile(JSON.stringify(dataToExport, null, 2), 'orders-export.json', 'application/json');
    } else if (format === 'pdf') {
      // Render a printable HTML view in a new window instead of calling
      // window.print() on the whole app (which prints navigation, sidebar, etc).
      const rows = dataToExport.map(o => `
        <tr>
          <td>${o.id}</td>
          <td>${o.customerName}</td>
          <td>${o.customerPhone}</td>
          <td>${o.society || ''}</td>
          <td>${o.address || ''}</td>
          <td>${o.category}</td>
          <td>${o.serviceType}</td>
          <td style="text-align:right">${formatCurrency(o.amount)}</td>
          <td>${o.orderStatus}</td>
          <td>${o.paymentMethod || ''}</td>
          <td>${o.paymentStatus}</td>
          <td>${o.pickupDate || ''}</td>
        </tr>`).join('');
      if (printWindow) {
        printWindow.document.write(`<!doctype html><html><head><title>Orders Export</title>
          <style>body{font-family:sans-serif;padding:20px}
          table{width:100%;border-collapse:collapse;font-size:12px}
          th,td{border:1px solid #ccc;padding:6px 8px;text-align:left}
          th{background:#f4f4f4}</style></head><body>
          <h2>Orders Export (${dataToExport.length})</h2>
          <table><thead><tr>
            <th>Order ID</th><th>Customer</th><th>Phone</th><th>Society</th><th>Flat/Address</th><th>Category</th>
            <th>Type</th><th>Amount</th><th>Status</th><th>Payment Method</th><th>Payment Status</th><th>Pickup</th>
          </tr></thead><tbody>${rows}</tbody></table>
          <script>window.onload = () => window.print();</script>
        </body></html>`);
        printWindow.document.close();
      }
    }
  };

  const downloadFile = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleServiceFilter = (type) => {
    setFilters((prev) => ({
      ...prev,
      serviceType: prev.serviceType.includes(type)
        ? prev.serviceType.filter((t) => t !== type)
        : [...prev.serviceType, type],
    }));
  };

  const toggleSourceFilter = (source) => {
    setFilters((prev) => ({
      ...prev,
      orderSource: prev.orderSource.includes(source)
        ? prev.orderSource.filter((s) => s !== source)
        : [...prev.orderSource, source],
    }));
  };

  const toggleCategoryFilter = (category) => {
    setFilters((prev) => ({
      ...prev,
      category: prev.category.includes(category)
        ? prev.category.filter((c) => c !== category)
        : [...prev.category, category],
    }));
  };

  const clearFilters = () => {
    setFilters({
      orderStatus: 'all',
      paymentStatus: 'all',
      serviceType: [],
      orderSource: [],
      category: [],
      startDate: '',
      endDate: '',
    });
  };

  const activeFiltersCount =
    (filters.orderStatus !== 'all' ? 1 : 0) +
    (filters.paymentStatus !== 'all' ? 1 : 0) +
    filters.serviceType.length +
    filters.orderSource.length +
    filters.category.length +
    (filters.startDate ? 1 : 0) +
    (filters.endDate ? 1 : 0);

  // Summary stats from server (accurate across all pages, cancelled excluded)
  const totalOrders = serverActiveTotal;
  const totalRevenue = serverOrderBookRevenue;
  const totalDiscount = serverOrderBookDiscount;
  const pendingOrders = filteredOrders.filter(o => o.orderStatus !== 'Delivered' && o.orderStatus !== 'Cancelled').length;

  return (
    <>
      <Header
        title="Order Management"
        subtitle="Track and manage all customer orders"
        onMenuClick={onMenuClick}
        actions={
          <div className="header-actions-group">
            <button className="header-icon-btn" onClick={handleRefresh} title="Refresh">
              <ArrowsClockwise size={18} />
            </button>
            <div className="export-wrapper">
              <button
                className="header-icon-btn"
                onClick={() => setShowExportMenu(!showExportMenu)}
                title="Export"
              >
                <Export size={18} />
              </button>
              {showExportMenu && (
                <div className="export-dropdown">
                  <div className="export-date-range">
                    <label>
                      From
                      <input
                        type="date"
                        value={exportRange.startDate}
                        onChange={(e) => setExportRange(prev => ({ ...prev, startDate: e.target.value }))}
                      />
                    </label>
                    <label>
                      To
                      <input
                        type="date"
                        value={exportRange.endDate}
                        onChange={(e) => setExportRange(prev => ({ ...prev, endDate: e.target.value }))}
                      />
                    </label>
                  </div>
                  <button disabled={exportLoading} onClick={() => handleExport('csv')}>
                    <FileXls size={18} /> {exportLoading ? 'Exporting...' : 'Export Excel (CSV)'}
                  </button>
                  <button disabled={exportLoading} onClick={() => handleExport('json')}>
                    <FileCsv size={18} /> {exportLoading ? 'Exporting...' : 'Export JSON'}
                  </button>
                  <button disabled={exportLoading} onClick={() => handleExport('pdf')}>
                    <FilePdf size={18} /> {exportLoading ? 'Exporting...' : 'Print / PDF'}
                  </button>
                </div>
              )}
            </div>
            <Button icon={<Plus size={18} />} onClick={handleOpenCreateOrder}>Create Order</Button>
          </div>
        }
      />
      <div className="page-content">
        {/* Stats Summary */}
        {hasPermission('dashboard') && (
          <div className="orders-stats">
            <div className="stat-card">
              <span className="stat-label">Total Orders</span>
              <span className="stat-value">{totalOrders}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Pending</span>
              <span className="stat-value stat-warning">{pendingOrders}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Total Revenue</span>
              <span className="stat-value stat-success">{formatCurrency(totalRevenue)}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Total Discounts</span>
              <span className="stat-value stat-danger">-{formatCurrency(totalDiscount)}</span>
            </div>
          </div>
        )}

        <div className="search-filter-bar">
          <div className="search-wrapper">
            <MagnifyingGlass className="search-icon" size={20} />
            <input
              type="text"
              className="search-input"
              placeholder="Search by Order ID, Customer Name or Phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button
            variant="secondary"
            icon={<Funnel size={18} />}
            onClick={() => setShowFilterModal(true)}
          >
            Filters
            {activeFiltersCount > 0 && (
              <span className="filter-badge">{activeFiltersCount}</span>
            )}
          </Button>
        </div>

        {activeFiltersCount > 0 && (
          <div className="active-filters">
            {filters.orderStatus !== 'all' && (
              <span className="filter-tag">
                Status: {filters.orderStatus}
                <X size={14} onClick={() => setFilters((prev) => ({ ...prev, orderStatus: 'all' }))} />
              </span>
            )}
            {filters.paymentStatus !== 'all' && (
              <span className="filter-tag">
                Payment: {filters.paymentStatus}
                <X size={14} onClick={() => setFilters((prev) => ({ ...prev, paymentStatus: 'all' }))} />
              </span>
            )}
            {filters.category.map((cat) => (
              <span key={cat} className="filter-tag">
                {cat}
                <X size={14} onClick={() => toggleCategoryFilter(cat)} />
              </span>
            ))}
            {filters.serviceType.map((type) => (
              <span key={type} className="filter-tag">
                {type}
                <X size={14} onClick={() => toggleServiceFilter(type)} />
              </span>
            ))}
            {filters.orderSource.map((source) => (
              <span key={source} className="filter-tag">
                {source}
                <X size={14} onClick={() => toggleSourceFilter(source)} />
              </span>
            ))}
            <button className="clear-filters-btn" onClick={clearFilters}>
              Clear All
            </button>
          </div>
        )}

        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <Button variant="secondary" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          </div>
        )}

        {loading ? (
          <div className="loading">
            <div className="spinner" />
            <span className="loading-text">Loading orders...</span>
          </div>
        ) : (
          <div className="orders-table-section">
            <div className="table-container">
              <table className="table orders-table scrollable">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Service</th>
                    <th>Customer</th>
                    <th>Location</th>
                    <th className="text-right">Amount</th>
                    <th>Schedule</th>
                    <th className="text-center">Order Status</th>
                    <th className="text-center">Payment</th>
                    <th className="text-center">Assigned</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="empty-state">
                        <div className="empty-state-icon">📦</div>
                        <p>No orders found</p>
                      </td>
                    </tr>
                  ) : (
                    filteredOrders.map((order) => (
                    <tr key={order.id}>
                      <td>
                        <button
                          className="order-id-link"
                          onClick={() => handleViewOrder(order)}
                          title="Click to view details"
                        >
                          {order.id}
                        </button>
                        <span className="order-date">{order.createdAt}</span>
                      </td>
                      <td>
                        <span className="service-name">{order.service}</span>
                        <span className="service-type">{order.serviceType}</span>
                      </td>
                      <td>
                        <span className="customer-name">{order.customerName}</span>
                        <span className="customer-phone">{order.customerPhone}</span>
                      </td>
                      <td className="location-cell">
                        {order.society}
                      </td>
                      <td className="text-right">
                        <span className="total-amount">{formatCurrency(order.amount)}</span>
                        {order.discount > 0 && (
                          <span className="discount-inline">
                            -{formatCurrency(order.discount)}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="schedule-date">{order.pickupDate}</span>
                        <span className="schedule-slot">{order.pickupSlot}</span>
                      </td>
                      <td className="text-center">
                        <div className="status-cell">
                          {getSourceBadge(order.orderSource)}
                          <span className={`badge badge-with-icon ${getStatusBadgeClass(order.orderStatus)}`}>
                            {getStatusIcon(order.orderStatus)}
                            {order.orderStatus}
                          </span>
                          {order.statusNeedsSync && (
                            <span className="live-indicator" title="Status auto-updated based on date">
                              <span className="live-dot"></span>
                              LIVE
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="text-center">
                        <span className={`badge ${getStatusBadgeClass(order.paymentStatus)}`}>
                          {order.paymentStatus}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className={`badge ${getStatusBadgeClass(order.assignedStatus)}`}>
                          {order.assignedStatus}
                        </span>
                      </td>
                      <td>
                        <div className="actions-cell">
                          <button
                            className="action-btn"
                            title="View Details"
                            onClick={() => handleViewOrder(order)}
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            className="action-btn"
                            title="Edit Order"
                            onClick={() => handleEditOrder(order)}
                          >
                            <PencilSimple size={16} />
                          </button>
                          <button
                            className="action-btn"
                            title="Apply Discount"
                            onClick={() => handleApplyDiscount(order)}
                          >
                            <Percent size={16} />
                          </button>
                          <button
                            className="action-btn"
                            title="Reschedule"
                            onClick={() => handleReschedule(order)}
                          >
                            <Calendar size={16} />
                          </button>
                          <button
                            className="action-btn"
                            title="Print"
                            onClick={() => handlePrint(order)}
                          >
                            <Printer size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>

            {/* Pagination sits outside the horizontal scroll so it stays visible */}
            {totalPages > 1 && (
              <div className="pagination-wrapper">
                <span className="pagination-info">
                  Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, ordersTotalCount)} of {ordersTotalCount} orders
                </span>
                <div className="pagination-controls" role="navigation" aria-label="Orders pagination">
                  <button
                    type="button"
                    className="pagination-btn"
                    onClick={() => goToPage(1)}
                    disabled={currentPage === 1}
                    aria-label="First page"
                  >
                    «
                  </button>
                  <button
                    type="button"
                    className="pagination-btn"
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    <CaretLeft size={16} weight="bold" />
                    <span>Prev</span>
                  </button>
                  {getPaginationItems().map((item, idx) =>
                    item === '...' ? (
                      <span key={`ellipsis-${idx}`} className="pagination-ellipsis" aria-hidden="true">…</span>
                    ) : (
                      <button
                        type="button"
                        key={item}
                        className={`pagination-btn${currentPage === item ? ' active' : ''}`}
                        onClick={() => goToPage(item)}
                        aria-label={`Page ${item}`}
                        aria-current={currentPage === item ? 'page' : undefined}
                      >
                        {item}
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    className="pagination-btn"
                    onClick={handleNextPage}
                    disabled={currentPage >= totalPages}
                    aria-label="Next page"
                  >
                    <span>Next</span>
                    <CaretRight size={16} weight="bold" />
                  </button>
                  <button
                    type="button"
                    className="pagination-btn"
                    onClick={() => goToPage(totalPages)}
                    disabled={currentPage >= totalPages}
                    aria-label="Last page"
                  >
                    »
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filter Modal */}
      <Modal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        title="Filter Orders"
        footer={
          <>
            <Button variant="secondary" onClick={clearFilters}>
              Clear Filters
            </Button>
            <Button onClick={() => setShowFilterModal(false)}>Apply Filters</Button>
          </>
        }
      >
        <div className="filter-section">
          <h4>Category</h4>
          <div className="filter-chips">
            {servicesList.map((service) => (
              <button
                key={service._id}
                className={`filter-chip ${filters.category.includes(service.categoryName) ? 'active' : ''}`}
                onClick={() => toggleCategoryFilter(service.categoryName)}
              >
                {service.categoryName}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-section">
          <h4>Service Type</h4>
          <div className="filter-chips">
            {serviceTypes.map((type) => (
              <button
                key={type}
                className={`filter-chip ${filters.serviceType.includes(type.toLowerCase()) ? 'active' : ''}`}
                onClick={() => toggleServiceFilter(type.toLowerCase())}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-section">
          <h4>Order Source</h4>
          <div className="filter-chips">
            {['Call', 'Walk-in', 'WhatsApp', 'App', 'Store Visit'].map((source) => (
              <button
                key={source}
                className={`filter-chip ${filters.orderSource.includes(source) ? 'active' : ''}`}
                onClick={() => toggleSourceFilter(source)}
              >
                {source}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-grid">
          <div className="form-group">
            <label className="form-label">Order Status</label>
            <select
              className="form-select"
              value={filters.orderStatus}
              onChange={(e) => setFilters((prev) => ({ ...prev, orderStatus: e.target.value }))}
            >
              <option value="all">All Statuses</option>
              {orderStatuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Payment Status</label>
            <select
              className="form-select"
              value={filters.paymentStatus}
              onChange={(e) => setFilters((prev) => ({ ...prev, paymentStatus: e.target.value }))}
            >
              <option value="all">All Payments</option>
              {paymentStatuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="filter-grid">
          <div className="form-group">
            <label className="form-label">Start Date</label>
            <input
              type="date"
              className="form-input"
              value={filters.startDate}
              onChange={(e) => setFilters((prev) => ({ ...prev, startDate: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label className="form-label">End Date</label>
            <input
              type="date"
              className="form-input"
              value={filters.endDate}
              onChange={(e) => setFilters((prev) => ({ ...prev, endDate: e.target.value }))}
            />
          </div>
        </div>
      </Modal>

      {/* Order Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title={`Order Details: ${selectedOrder?.id || ''}`}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => handlePrint(selectedOrder)}>
              <Printer size={16} /> Print
            </Button>
            <Button variant="secondary" onClick={() => handlePrintTags(selectedOrder)}>
              <Tag size={16} /> Print Tags
            </Button>
            <Button variant="secondary" onClick={() => {
              setShowDetailsModal(false);
              handleEditOrder(selectedOrder);
            }}>
              <PencilSimple size={16} /> Edit
            </Button>
            {(selectedOrder?.orderStatus === 'New' || selectedOrder?.orderStatus === 'In-Progress' || selectedOrder?.orderStatus === 'Pick-up Pending') && (
              <Button variant="secondary" onClick={() => {
                setShowDetailsModal(false);
                handleOpenAddService(selectedOrder);
              }}>
                <Plus size={16} /> Add Service
              </Button>
            )}
            {selectedOrder?.orderStatus !== 'Cancelled' && selectedOrder?.orderStatus !== 'Delivered' && (
              <Button variant="danger" onClick={handleCancelOrder}>
                <XCircle size={16} /> Cancel Order
              </Button>
            )}
            <Button onClick={() => setShowDetailsModal(false)}>Close</Button>
          </>
        }
      >
        {selectedOrder && (
          <div className="order-details">
            {/* Order Source + Status Banner */}
            <div className="order-banner-row">
              {getSourceBadge(selectedOrder.orderSource)}
              <div className={`order-status-banner status-${selectedOrder.orderStatus.toLowerCase().replace(/\s+/g, '-')}`}>
                {getStatusIcon(selectedOrder.orderStatus)}
                <span>{selectedOrder.orderStatus}</span>
              </div>
            </div>

            <div className="details-grid">
              <div className="details-section">
                <h4><Phone size={18} /> Customer Information</h4>
                <div className="info-card">
                  <p><strong>Name:</strong> {selectedOrder.customerName}</p>
                  <p><strong>Phone:</strong> {selectedOrder.customerPhone}</p>
                  <p><MapPin size={14} /> {selectedOrder.society}, {selectedOrder.address}</p>
                  <p><strong>Pincode:</strong> {selectedOrder.pincode}</p>
                  <p><strong>Order Source:</strong> <span className="badge badge-neutral">{selectedOrder.orderSource}</span></p>
                </div>
              </div>

              <div className="details-section">
                <h4><Tag size={18} /> Quick Status Update</h4>
                <div className="form-group">
                  <label className="form-label">Order Status</label>
                  <select
                    className="form-select"
                    value={selectedOrder.orderStatus}
                    onChange={(e) => handleUpdateOrderStatus(e.target.value)}
                  >
                    {orderStatuses.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Payment Status</label>
                  <select
                    className="form-select"
                    value={selectedOrder.paymentStatus}
                    onChange={(e) => handleUpdatePaymentStatus(e.target.value)}
                  >
                    {paymentStatuses.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedOrder.assignedTo && (
                  <p><strong>Assigned To:</strong> {selectedOrder.assignedTo}</p>
                )}
              </div>
            </div>

            <hr />

            <h4><Calendar size={18} /> Schedule & Service</h4>
            <div className="schedule-info-grid">
              <div className="info-block">
                <span className="info-label">Category</span>
                <span className={`badge ${getCategoryBadgeClass(selectedOrder.category)}`}>
                  {selectedOrder.category}
                </span>
              </div>
              <div className="info-block">
                <span className="info-label">Service</span>
                <span className="info-value">{selectedOrder.service}</span>
              </div>
              <div className="info-block">
                <span className="info-label">Type</span>
                <span className="info-value">
                  {(selectedOrder.serviceTypeList || [selectedOrder.serviceType]).filter(Boolean).join(', ')}
                </span>
              </div>
              <div className="info-block">
                <span className="info-label">Pickup</span>
                <span className="info-value">{selectedOrder.pickupDate}</span>
                <span className="info-sub">{selectedOrder.pickupSlot}</span>
              </div>
              <div className="info-block">
                <span className="info-label">Delivery</span>
                <span className="info-value">{selectedOrder.deliveryDate}</span>
              </div>
            </div>

            <hr />

            <h4><CurrencyInr size={18} /> Order Items</h4>
            <div className="items-list">
              {selectedOrder.items.length === 0 && (selectedOrder.addons || []).length === 0 ? (
                <div className="items-empty">No items yet. Use Add Service to add garments or addons.</div>
              ) : null}
              {selectedOrder.items.map((item, index) => (
                <div key={index} className="item-row">
                  <span className="item-name">
                    {item.category && !item.isWeight && !item.isPair ? (
                      <span className="item-category">{item.category}{item.serviceType ? ` · ${item.serviceType}` : ''}</span>
                    ) : null}
                    {item.name}{item.garmentCode ? ` (${item.garmentCode})` : ''}
                  </span>
                  <span className="item-qty">
                    {item.isWeight || item.isPair ? item.qty : `×${item.qty}`}
                  </span>
                  <span className="item-price">
                    {formatCurrency(item.itemTotal || (item.price * (typeof item.qty === 'number' ? item.qty : 1)))}
                  </span>
                </div>
              ))}
              {(selectedOrder.addons || []).map((addon, index) => (
                <div key={`addon-${index}`} className="item-row item-row--addon">
                  <span className="item-name">
                    <span className="item-category">Addon</span>
                    {addon.name}
                  </span>
                  <span className="item-qty">×{addon.qty}</span>
                  <span className="item-price">
                    {formatCurrency(addon.itemTotal || (addon.price * addon.qty))}
                  </span>
                </div>
              ))}
            </div>

            <div className="order-summary">
              {(() => {
                const subtotal = selectedOrder.subtotal || 0;
                const discount = selectedOrder.discount || 0;
                const addonsTotal = selectedOrder.addonsTotal || 0;
                const total = selectedOrder.amount || 0;

                return (
                  <>
                    <div className="summary-row">
                      <span>Subtotal</span>
                      <span>{formatCurrency(subtotal)}</span>
                    </div>
                    {discount > 0 && (
                      <div className="summary-row discount-row">
                        <span>
                          Discount
                          {selectedOrder.discountCode && (
                            <span className="discount-code">({selectedOrder.discountCode})</span>
                          )}
                        </span>
                        <span className="discount-value">-{formatCurrency(discount)}</span>
                      </div>
                    )}
                    {addonsTotal > 0 && (
                      <div className="summary-row">
                        <span>Addons</span>
                        <span>{formatCurrency(addonsTotal)}</span>
                      </div>
                    )}
                    <div className="summary-row total-row">
                      <span>Total</span>
                      <span className="total-value">{formatCurrency(total)}</span>
                    </div>
                  </>
                );
              })()}
            </div>

            {selectedOrder.notes && (
              <>
                <hr />
                <h4><Note size={18} /> Notes</h4>
                <div className="order-notes">
                  {selectedOrder.notes}
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Edit Order Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title={`Edit Order: ${selectedOrder?.id || ''}`}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>
              <FloppyDisk size={16} /> Save Changes
            </Button>
          </>
        }
      >
        {selectedOrder && (
          <div className="edit-order-form">
            <div className="edit-section">
              <h4>Service Details</h4>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select
                    className="form-select"
                    value={editFormData.category}
                    onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                  >
                    {servicesList.map((service) => (
                      <option key={service._id} value={service.categoryName}>{service.categoryName}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Service Type</label>
                  <select
                    className="form-select"
                    value={editFormData.serviceType}
                    onChange={(e) => setEditFormData({ ...editFormData, serviceType: e.target.value })}
                  >
                    {serviceTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Service Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={editFormData.service}
                  onChange={(e) => setEditFormData({ ...editFormData, service: e.target.value })}
                />
              </div>
            </div>

            <div className="edit-section">
              <h4>Status & Assignment</h4>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Order Status</label>
                  <select
                    className="form-select"
                    value={editFormData.orderStatus}
                    onChange={(e) => setEditFormData({ ...editFormData, orderStatus: e.target.value })}
                  >
                    {orderStatuses.map((status) => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Payment Status</label>
                  <select
                    className="form-select"
                    value={editFormData.paymentStatus}
                    onChange={(e) => setEditFormData({ ...editFormData, paymentStatus: e.target.value })}
                  >
                    {paymentStatuses.map((status) => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Order Source</label>
                <select
                  className="form-select"
                  value={editFormData.orderSource}
                  onChange={(e) => setEditFormData({ ...editFormData, orderSource: e.target.value })}
                >
                  {['Call', 'Walk-in', 'WhatsApp', 'App', 'Store Visit'].map((src) => (
                    <option key={src} value={src}>{src}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Pickup Executive <span className="required-star">*</span></label>
                <select
                  className="form-select"
                  value={editFormData.pickupAgent}
                  onChange={(e) => setEditFormData({ ...editFormData, pickupAgent: e.target.value })}
                >
                  <option value="">-- Assign Pickup Executive --</option>
                  {staffList.map((staff) => (
                    <option key={staff._id} value={staff._id}>{staff.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Delivery Executive <span className="required-star">*</span></label>
                <select
                  className="form-select"
                  value={editFormData.deliveryAgent}
                  onChange={(e) => setEditFormData({ ...editFormData, deliveryAgent: e.target.value })}
                >
                  <option value="">-- Assign Delivery Executive --</option>
                  {staffList.map((staff) => (
                    <option key={staff._id} value={staff._id}>{staff.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="edit-section">
              <h4>Schedule</h4>
              <div className="form-row three-col">
                <div className="form-group">
                  <label className="form-label">Pickup Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={editFormData.pickupDate}
                    onChange={(e) => setEditFormData({ ...editFormData, pickupDate: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Pickup Slot</label>
                  <select
                    className="form-select"
                    value={editFormData.pickupSlotId}
                    onChange={(e) => {
                      const slot = pickupSlotsFullList.find(s => s._id === e.target.value);
                      setEditFormData({ ...editFormData, pickupSlotId: e.target.value, pickupSlot: slot?.slotName || '' });
                    }}
                  >
                    <option value="">-- Select slot --</option>
                    {pickupSlotsFullList.map((slot) => (
                      <option key={slot._id} value={slot._id}>{slot.slotName}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Delivery Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={editFormData.deliveryDate}
                    onChange={(e) => setEditFormData({ ...editFormData, deliveryDate: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="edit-section">
              <div className="section-header">
                <h4>Order Items</h4>
                <span className="items-hint">Use "Add Service" from order details to add items.</span>
              </div>
              <div className="items-edit-list items-edit-readonly">
                {editFormData.items.map((item, index) => (
                  <div key={index} className="item-edit-row">
                    <span className="item-name">
                      {item.name}{item.garmentCode ? ` (${item.garmentCode})` : ''}
                    </span>
                    <span className="item-qty">
                      {item.isWeight || item.isPair ? item.qty : `× ${item.qty}`}
                    </span>
                    <span className="item-total">
                      {formatCurrency(item.itemTotal || (item.price * (typeof item.qty === 'number' ? item.qty : 1)))}
                    </span>
                  </div>
                ))}
              </div>
              <div className="items-subtotal">
                <span>Subtotal:</span>
                <span>{formatCurrency(calculateSubtotal(editFormData.items))}</span>
              </div>
            </div>

            <div className="edit-section">
              <h4>Notes</h4>
              <textarea
                className="form-input"
                rows={3}
                placeholder="Add order notes..."
                value={editFormData.notes}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Discount Modal */}
      <Modal
        isOpen={showDiscountModal}
        onClose={() => setShowDiscountModal(false)}
        title={`Apply Discount: ${selectedOrder?.id || ''}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowDiscountModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveDiscount}>Apply Discount</Button>
          </>
        }
      >
        {selectedOrder && (
          <div className="discount-form">
            <div className="current-order-info">
              <p><strong>Order:</strong> {selectedOrder.id}</p>
              <p><strong>Subtotal:</strong> {formatCurrency(selectedOrder.subtotal)}</p>
              {selectedOrder.discount > 0 && (
                <p><strong>Current Discount:</strong> -{formatCurrency(selectedOrder.discount)}</p>
              )}
            </div>

            <hr />

            <div className="form-group">
              <label className="form-label">Discount Type</label>
              <div className="discount-type-group">
                <label className={`discount-type-option ${discountData.type === 'percent' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="discountType"
                    value="percent"
                    checked={discountData.type === 'percent'}
                    onChange={(e) => setDiscountData((prev) => ({ ...prev, type: e.target.value }))}
                  />
                  <Percent size={16} />
                  <span>Percentage</span>
                </label>
                <label className={`discount-type-option ${discountData.type === 'flat' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="discountType"
                    value="flat"
                    checked={discountData.type === 'flat'}
                    onChange={(e) => setDiscountData((prev) => ({ ...prev, type: e.target.value }))}
                  />
                  <CurrencyInr size={16} />
                  <span>Flat Amount</span>
                </label>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                {discountData.type === 'percent' ? 'Discount Percentage (%)' : 'Discount Amount (₹)'}
              </label>
              <input
                type="number"
                className="form-input"
                placeholder={discountData.type === 'percent' ? 'e.g., 10' : 'e.g., 100'}
                min="0"
                step="0.01"
                max={discountData.type === 'percent' ? 100 : selectedOrder.subtotal}
                value={discountData.value}
                onChange={(e) => setDiscountData((prev) => ({ ...prev, value: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Discount Code (Optional)</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., WELCOME100"
                value={discountData.code}
                onChange={(e) => setDiscountData((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
              />
            </div>

            {discountData.value && (
              <div className="discount-preview">
                <span>New Total:</span>
                <span className="preview-amount">
                  {formatCurrency(
                    selectedOrder.subtotal -
                    (discountData.type === 'percent'
                      ? (selectedOrder.subtotal * parseFloat(discountData.value || 0)) / 100
                      : parseFloat(discountData.value || 0)) +
                    (selectedOrder.addonsTotal || 0)
                  )}
                </span>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Reschedule Modal */}
      <Modal
        isOpen={showRescheduleModal}
        onClose={() => setShowRescheduleModal(false)}
        title={`Reschedule Order: ${selectedOrder?.id || ''}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowRescheduleModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveReschedule}>Save Changes</Button>
          </>
        }
      >
        {selectedOrder && (
          <div className="reschedule-form">
            <div className="form-group">
              <label className="form-label">Pickup Date</label>
              <input
                type="date"
                className="form-input"
                value={rescheduleData.pickupDate}
                onChange={async (e) => {
                  const date = e.target.value;
                  setRescheduleData((prev) => ({ ...prev, pickupDate: date, pickupSlot: '' }));
                  if (date) {
                    try {
                      const slots = await slotService.getSlotStatus(date);
                      setRescheduleSlots((slots || []).filter(s => s.slotType === 'Pickup' && s.isActive !== false));
                    } catch { setRescheduleSlots([]); }
                  } else {
                    setRescheduleSlots([]);
                  }
                }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Pickup Slot</label>
              <select
                className="form-select"
                value={rescheduleData.pickupSlot}
                onChange={(e) => setRescheduleData((prev) => ({ ...prev, pickupSlot: e.target.value }))}
              >
                <option value="">-- Select slot --</option>
                {(rescheduleSlots.length > 0 ? rescheduleSlots : pickupSlotsFullList).map((slot) => {
                  const available = (slot.maxCapacity || 0) - (slot.bookedCount || 0);
                  const isFull = available <= 0;
                  return (
                    <option key={slot._id} value={slot._id} disabled={isFull}>
                      {slot.slotName} — {isFull ? 'Not Available' : `${slot.bookedCount || 0}/${slot.maxCapacity} booked (${available} left)`}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Delivery Date</label>
              <input
                type="date"
                className="form-input"
                value={rescheduleData.deliveryDate}
                onChange={(e) => setRescheduleData((prev) => ({ ...prev, deliveryDate: e.target.value }))}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Payment Modal (Req #10) — mandatory when marking Delivered */}
      <Modal
        isOpen={showPaymentModal}
        onClose={() => { setShowPaymentModal(false); setPendingStatusOrderId(null); }}
        title="Payment Required — Mark as Delivered"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setShowPaymentModal(false); setPendingStatusOrderId(null); }}>
              Cancel
            </Button>
            <Button onClick={handleSavePaymentAndStatus}>
              Confirm Payment &amp; Deliver
            </Button>
          </>
        }
      >
        <div className="payment-form">
          <p className="payment-info-text">
            Order cannot be marked as <strong>Delivered</strong> without payment confirmation.
          </p>
          <div className="form-group">
            <label className="form-label">Payment Method</label>
            <div className="radio-group-inline">
              {['Cash', 'UPI'].map((m) => (
                <label key={m} className={`radio-pill ${paymentFormData.method === m ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="payMethod"
                    value={m}
                    checked={paymentFormData.method === m}
                    onChange={(e) => setPaymentFormData({ ...paymentFormData, method: e.target.value, transactionID: '' })}
                  />
                  {m}
                </label>
              ))}
            </div>
          </div>
          {paymentFormData.method === 'UPI' && (
            <div className="form-group">
              <label className="form-label">Transaction ID <span className="required-star">*</span></label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter UPI Transaction ID"
                value={paymentFormData.transactionID}
                onChange={(e) => setPaymentFormData({ ...paymentFormData, transactionID: e.target.value })}
              />
            </div>
          )}
        </div>
      </Modal>

      {/* Create Order Modal */}
      <Modal
        isOpen={showCreateOrderModal}
        onClose={() => setShowCreateOrderModal(false)}
        title="Create New Order"
        size="xl"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreateOrderModal(false)}>Cancel</Button>
            <Button onClick={handleCreateOrderSubmit} loading={createOrderSubmitting} disabled={createOrderStagedServices.length === 0 || (!createCustomer && !(isNewCustomer && createNewCustomerName.trim()))}>
              Create Order {createOrderStagedServices.length > 0 ? `(${createOrderStagedServices.length} service${createOrderStagedServices.length > 1 ? 's' : ''})` : ''}
            </Button>
          </>
        }
      >
        <div className="order-booking-form">
          {/* Phone lookup */}
          <div className="form-group">
            <label className="form-label">Customer Phone Number</label>
            <div style={{ position: 'relative' }}>
              <input
                type="tel"
                className="form-input"
                placeholder="Enter 10-digit mobile number"
                value={createPhone}
                onChange={(e) => handleCreatePhoneChange(e.target.value)}
                maxLength={10}
              />
              {createPhoneLookupLoading && (
                <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: 'var(--text-secondary)' }}>Looking up…</span>
              )}
            </div>
            {createPhoneError && <div style={{ color: 'var(--danger)', fontSize: 13, marginTop: 4 }}>{createPhoneError}</div>}
          </div>

          {/* New customer — enter name inline */}
          {isNewCustomer && (
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">Customer Name <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(New Customer)</span></label>
              <input
                className="form-input"
                placeholder="Enter customer name"
                value={createNewCustomerName}
                onChange={e => setCreateNewCustomerName(e.target.value)}
                autoFocus
              />
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                No existing customer found — a new customer will be created automatically.
              </div>
            </div>
          )}

          {/* Customer info (auto-filled) */}
          {createCustomer && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Customer Name</label>
                  <input className="form-input" value={createCustomer.customerName || createCustomer.name || ''} readOnly style={{ background: 'var(--bg-secondary)', cursor: 'not-allowed' }} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Delivery Address</label>
                  {createCustomer.addresses && createCustomer.addresses.length > 0 ? (
                    <select
                      className="form-select"
                      value={createOrderFormData.selectedAddressIdx}
                      onChange={(e) => setCreateOrderFormData(prev => ({ ...prev, selectedAddressIdx: parseInt(e.target.value) }))}
                    >
                      {createCustomer.addresses.map((addr, idx) => (
                        <option key={idx} value={idx}>
                          {addr.address}{addr.isCurrent ? ' (Current)' : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input className="form-input" value="No address on file" readOnly style={{ background: 'var(--bg-secondary)', cursor: 'not-allowed' }} />
                  )}
                </div>
              </div>

              {/* Source, Pickup, Delivery */}
              <div className="booking-header-row">
                <div className="form-group compact">
                  <label className="form-label">Source</label>
                  <div className="radio-group-inline">
                    {['Call', 'Walk-in', 'App', 'Store Visit'].map(src => (
                      <label key={src} className={`radio-pill ${createOrderFormData.orderSource === src ? 'active' : ''}`}>
                        <input type="radio" name="co-source" value={src} checked={createOrderFormData.orderSource === src} onChange={() => {
                            const isWalkIn = src === 'Walk-in' || src === 'Store Visit';
                            setCreateOrderFormData(prev => ({
                              ...prev,
                              orderSource: src,
                              ...(isWalkIn ? { isPickupScheduled: false, pickupDate: '', pickupSlot: '', deliveryType: 'Store Pick-up' } : { isPickupScheduled: true, deliveryType: 'Home Delivery' }),
                            }));
                          }} />
                        {src}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="form-group compact">
                  <label className="form-label">Pickup</label>
                  <div className="radio-group-inline">
                    <label className={`radio-pill ${createOrderFormData.isPickupScheduled ? 'active' : ''}`}>
                      <input type="radio" name="co-pickup" checked={createOrderFormData.isPickupScheduled} onChange={() => setCreateOrderFormData(prev => ({ ...prev, isPickupScheduled: true }))} />
                      Schedule
                    </label>
                    <label className={`radio-pill ${!createOrderFormData.isPickupScheduled ? 'active' : ''}`}>
                      <input type="radio" name="co-pickup" checked={!createOrderFormData.isPickupScheduled} onChange={() => setCreateOrderFormData(prev => ({ ...prev, isPickupScheduled: false, pickupDate: '', pickupSlot: '' }))} />
                      Walk-in
                    </label>
                  </div>
                </div>
                <div className="form-group compact">
                  <label className="form-label">Delivery</label>
                  <div className="radio-group-inline">
                    <label className={`radio-pill ${createOrderFormData.deliveryType === 'Home Delivery' ? 'active' : ''}`}>
                      <input type="radio" name="co-delivery" value="Home Delivery" checked={createOrderFormData.deliveryType === 'Home Delivery'} onChange={(e) => setCreateOrderFormData(prev => ({ ...prev, deliveryType: e.target.value }))} />
                      Home
                    </label>
                    <label className={`radio-pill ${createOrderFormData.deliveryType === 'Store Pick-up' ? 'active' : ''}`}>
                      <input type="radio" name="co-delivery" value="Store Pick-up" checked={createOrderFormData.deliveryType === 'Store Pick-up'} onChange={(e) => setCreateOrderFormData(prev => ({ ...prev, deliveryType: e.target.value }))} />
                      Store
                    </label>
                  </div>
                </div>
              </div>

              {/* Pickup Date/Slot */}
              {createOrderFormData.isPickupScheduled && (
                <div className="pickup-schedule-row">
                  <div className="form-group compact">
                    <label className="form-label">Pickup Date</label>
                    <input type="date" className="form-input" value={createOrderFormData.pickupDate} min={new Date().toISOString().split('T')[0]} onChange={(e) => handleCreateOrderPickupDate(e.target.value)} />
                  </div>
                  <div className="form-group compact">
                    <label className="form-label">Slot</label>
                    <select className="form-select" value={createOrderFormData.pickupSlot} onChange={(e) => setCreateOrderFormData(prev => ({ ...prev, pickupSlot: e.target.value }))}>
                      <option value="">Select slot</option>
                      {createOrderSlots.map((slot) => {
                        const available = slot.maxCapacity - slot.bookedCount;
                        const isFull = available <= 0;
                        return (
                          <option key={slot._id} value={slot._id} disabled={isFull}>
                            {slot.slotName} — {isFull ? 'Not Available' : `${slot.bookedCount}/${slot.maxCapacity} booked (${available} left)`}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              )}

              {/* Service + Type */}
              <div className="service-selection-row">
                <div className="form-group">
                  <label className="form-label">Service</label>
                  <select className="form-select" value={createOrderFormData.serviceCategory} onChange={(e) => {
                    const newId = e.target.value;
                    setCreateOrderFormData(prev => ({ ...prev, serviceCategory: newId }));
                    setCreateOrderItemQtys({});
                    const svc = createOrderServices.find(s => s._id === newId);
                    const firstCat = svc?.subcategories?.[0]?.garmentCategory;
                    setCreateOrderGarmentCat(firstCat || 'Men');
                  }}>
                    <option value="">Select service</option>
                    {createOrderServices.map(s => <option key={s._id} value={s._id}>{s.categoryName}</option>)}
                  </select>
                </div>
                <div className="service-type-tabs">
                  {['Standard', 'Semi Express', 'Express'].map((type) => (
                    <button key={type} type="button" className={`service-type-tab ${createOrderFormData.serviceType === type ? 'active' : ''}`} onClick={() => handleCreateServiceTypeChange(type)}>
                      {type}
                      {type === 'Semi Express' && <span className="multiplier">1.5x</span>}
                      {type === 'Express' && <span className="multiplier">2x</span>}
                    </button>
                  ))}
                </div>
              </div>

              {/* Delivery date info */}
              {createOrderFormData.pickupDate && createOrderFormData.deliveryDate && (
                <div className="delivery-date-info">
                  <div className="date-info-item">
                    <span className="date-label">Pickup</span>
                    <span className="date-value">{new Date(createOrderFormData.pickupDate).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                  </div>
                  <div className="date-arrow">→</div>
                  <div className="date-info-item">
                    <span className="date-label">Est. Delivery</span>
                    <span className="date-value highlight">{new Date(createOrderFormData.deliveryDate).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                  </div>
                  <span className="delivery-type-badge">
                    {createOrderFormData.serviceType === 'Express' ? '1 Day' : createOrderFormData.serviceType === 'Semi Express' ? '2 Days' : '3 Days'}
                  </span>
                </div>
              )}

              {/* PerKg */}
              {getCreateSelectedService()?.pricingModel === 'PerKg' && (
                <div className="perkg-section">
                  <div className="form-group">
                    <label className="form-label">Weight (kg)</label>
                    <input type="number" className="form-input" placeholder="Enter weight" min="0" step="0.1" value={createOrderItemQtys['weight'] || ''} onChange={(e) => setCreateOrderItemQtys(prev => ({ ...prev, weight: e.target.value }))} />
                  </div>
                  <div className="perkg-price">
                    Price: ₹{((getCreateSelectedService()?.pricePerKg || 0) * getCreatePriceMultiplier()).toFixed(2)} per kg
                    {parseFloat(createOrderItemQtys['weight'] || 0) > 0 && (
                      <div style={{ marginTop: 4, fontWeight: 600, color: 'var(--primary)' }}>
                        Total: ₹{(parseFloat(createOrderItemQtys['weight']) * (getCreateSelectedService()?.pricePerKg || 0) * getCreatePriceMultiplier()).toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* PerItem */}
              {getCreateSelectedService()?.pricingModel === 'PerItem' && (
                <div className="items-selection-section">
                  <div className="item-search-box">
                    <MagnifyingGlass className="search-icon" size={16} />
                    <input type="text" className="form-input" placeholder="Search items..." value={createOrderItemSearch} onChange={(e) => setCreateOrderItemSearch(e.target.value)} />
                  </div>
                  <div className="garment-category-tabs">
                    {getCreateServiceGarmentCategories().map(cat => (
                      <button key={cat} type="button" className={`garment-tab ${createOrderGarmentCat === cat ? 'active' : ''}`} onClick={() => setCreateOrderGarmentCat(cat)}>{cat}</button>
                    ))}
                  </div>
                  <div className="items-grid">
                    {getCreateFilteredSubcategories().length === 0 ? (
                      <div className="no-items-message">No items found</div>
                    ) : (
                      getCreateFilteredSubcategories().map(item => {
                        const qtyKey = item.garmentCode || item.itemName;
                        const qty = createOrderItemQtys[qtyKey] || 0;
                        const price = (item.price * getCreatePriceMultiplier()).toFixed(2);
                        return (
                          <div key={qtyKey} className={`item-card ${qty > 0 ? 'selected' : ''}`}>
                            <div className="item-card-header">
                              <span className="item-name">{item.itemName}</span>
                              <span className="item-code">{item.garmentCode}</span>
                            </div>
                            <div className="item-card-footer">
                              <span className="item-price">₹{price}</span>
                              <div className="qty-controls">
                                <button type="button" className="qty-btn" disabled={qty === 0} onClick={() => setCreateOrderItemQtys(prev => ({ ...prev, [qtyKey]: Math.max(0, qty - 1) }))}>−</button>
                                <span className="qty-input">{qty}</span>
                                <button type="button" className="qty-btn" onClick={() => setCreateOrderItemQtys(prev => ({ ...prev, [qtyKey]: qty + 1 }))}>+</button>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}

              {/* PerPair */}
              {getCreateSelectedService()?.pricingModel === 'PerPair' && (
                <div className="perkg-section">
                  <div className="form-group">
                    <label className="form-label">Number of Pairs</label>
                    <input type="number" className="form-input" placeholder="Enter pairs" min="1" value={createOrderItemQtys['pairs'] || ''} onChange={(e) => setCreateOrderItemQtys(prev => ({ ...prev, pairs: e.target.value }))} />
                  </div>
                  <div className="perkg-price">
                    Price: ₹{((getCreateSelectedService()?.pricePerPair || 0) * getCreatePriceMultiplier()).toFixed(2)} per pair
                    {parseFloat(createOrderItemQtys['pairs'] || 0) > 0 && (
                      <div style={{ marginTop: 4, fontWeight: 600, color: 'var(--primary)' }}>
                        Total: ₹{(parseFloat(createOrderItemQtys['pairs']) * (getCreateSelectedService()?.pricePerPair || 0) * getCreatePriceMultiplier()).toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Add to Order button */}
              {getCreateSelectedService() && (
                <div style={{ marginTop: 16 }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ width: '100%' }}
                    onClick={handleStageCurrentService}
                  >
                    + Add to Order
                  </button>
                </div>
              )}

              {/* Staged services list */}
              {createOrderStagedServices.length > 0 && (
                <div className="staged-services-list">
                  <div className="staged-services-header">Services in this order</div>
                  {createOrderStagedServices.map((entry, index) => (
                    <div key={index} className="staged-service-row">
                      <div className="staged-service-info">
                        <span className="staged-service-name">{entry.serviceName}</span>
                        <span className={`badge ${getStatusBadgeClass(entry.serviceType)}`} style={{ fontSize: 11 }}>{entry.serviceType}</span>
                        <span className="staged-service-summary">
                          {entry.pricingModel === 'PerKg' && `${entry.items[0]?.weightInKg} kg`}
                          {entry.pricingModel === 'PerPair' && `${entry.items[0]?.pairCount} pairs`}
                          {entry.pricingModel === 'PerItem' && `${entry.items.reduce((s, i) => s + i.quantity, 0)} items`}
                        </span>
                      </div>
                      <div className="staged-service-actions">
                        <span className="staged-service-subtotal">₹{entry.subtotal.toFixed(2)}</span>
                        <button
                          type="button"
                          className="staged-remove-btn"
                          onClick={() => handleRemoveStagedService(index)}
                          title="Remove"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="staged-total-row">
                    <span>Services</span>
                    <span>₹{createOrderStagedServices.reduce((s, e) => s + e.subtotal, 0).toFixed(2)}</span>
                  </div>
                  {Object.keys(createOrderSelectedAddons).length > 0 && (
                    <div className="staged-total-row">
                      <span>Addons</span>
                      <span>₹{calculateAddonsTotal(createOrderSelectedAddons).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="staged-total-row is-grand">
                    <span>Grand Total</span>
                    <span>₹{(
                      createOrderStagedServices.reduce((s, e) => s + e.subtotal, 0) +
                      calculateAddonsTotal(createOrderSelectedAddons)
                    ).toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Addons can be selected even before staging services — show section always once customer picked */}
              {renderAddonsSection(createOrderSelectedAddons, setCreateOrderSelectedAddons)}
              {createOrderStagedServices.length === 0 && Object.keys(createOrderSelectedAddons).length > 0 && (
                <div className="addons-only-hint">
                  Add at least one service before creating the order. Addons alone cannot form an order.
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      {/* Add Service Modal */}
      <Modal
        isOpen={showAddServiceModal}
        onClose={() => !addServiceSaving && handleCloseAddService()}
        title="Add New Service"
        size="lg"
        footer={
          <>
            <div className="add-service-footer-total">
              <span>Adding</span>
              <strong>{formatCurrency(calculateAddServiceGrandTotal())}</strong>
            </div>
            <Button variant="secondary" onClick={handleCloseAddService} disabled={addServiceSaving}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveAddedServices}
              loading={addServiceSaving}
              disabled={addServiceSaving || (addedServiceItems.length === 0 && !hasPendingQuantities() && Object.keys(addedServiceAddons).length === 0)}
            >
              Save to Order
            </Button>
          </>
        }
      >
        <div className="add-service-modal">
          {/* Service Type Selection */}
          <div className="service-type-section">
            <label className="form-label">Service Type</label>
            <div className="service-type-tabs">
              {serviceTypes.map((type) => (
                <button
                  key={type}
                  className={`service-type-tab ${selectedServiceType === type ? 'active' : ''}`}
                  onClick={() => setSelectedServiceType(type)}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Service Category Dropdown */}
          <div className="form-group">
            <label className="form-label">Service Category</label>
            <select
              className="form-select"
              value={selectedServiceCategory}
              onChange={(e) => setSelectedServiceCategory(e.target.value)}
            >
              <option value="">Select a category</option>
              {servicesList.map((service) => (
                <option key={service._id} value={service._id}>
                  {service.categoryName} ({service.serviceCode || ''})
                </option>
              ))}
            </select>
          </div>

          {/* Show content based on service pricing model */}
          {getSelectedService() && (
            <>
              <div className="adding-info">
                Adding: <strong>{getSelectedService()?.categoryName}</strong> - {selectedServiceType}
              </div>

              {/* For PerItem services, show search and item list */}
              {getSelectedService()?.pricingModel === 'PerItem' && (
                <>
                  {/* Search Box */}
                  <div className="item-search-wrapper">
                    <MagnifyingGlass className="search-icon" size={18} />
                    <input
                      type="text"
                      className="form-input item-search-input"
                      placeholder="Search items with Item Code, Name..."
                      value={itemSearchQuery}
                      onChange={(e) => setItemSearchQuery(e.target.value)}
                    />
                  </div>

                  {/* Garment Category Tabs */}
                  <div className="garment-category-tabs">
                    {getServiceGarmentCategories().map((cat) => (
                      <button
                        key={cat}
                        className={`garment-tab ${selectedGarmentCategory === cat ? 'active' : ''}`}
                        onClick={() => setSelectedGarmentCategory(cat)}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>

                  {/* Items List */}
                  <div className="items-selection-list">
                    {getFilteredSubcategories().length === 0 ? (
                      <div className="no-items-message">
                        No items found in this category
                      </div>
                    ) : (
                      getFilteredSubcategories().map((item) => {
                        const multiplier = getPriceMultiplier(getSelectedService());
                        const price = (item.price * multiplier).toFixed(2);
                        const qtyKey = item.garmentCode || item.itemName;
                        return (
                          <div key={qtyKey} className="item-selection-row">
                            <div className="item-info">
                              <span className="item-name">
                                {item.itemName} {item.garmentCode && `(${item.garmentCode})`}
                              </span>
                            </div>
                            <div className="item-price">{price}</div>
                            <input
                              type="number"
                              className="item-qty-input"
                              min="0"
                              step="1"
                              value={itemQuantities[qtyKey] || 0}
                              onChange={(e) => handleQuantityChange(qtyKey, e.target.value)}
                            />
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Add Items Button */}
                  <div className="add-items-action">
                    <Button
                      onClick={handleAddItems}
                      disabled={Object.values(itemQuantities).every(q => !q || q === 0)}
                    >
                      <Plus size={16} /> Add Item(s)
                    </Button>
                  </div>
                </>
              )}

              {/* For PerKg services */}
              {getSelectedService()?.pricingModel === 'PerKg' && (
                <div className="perkg-input-section">
                  <div className="form-group">
                    <label className="form-label">Weight (kg)</label>
                    <input
                      type="number"
                      className="form-input"
                      min="0"
                      step="0.1"
                      placeholder="Enter weight"
                      value={itemQuantities['weight'] || ''}
                      onChange={(e) => handleQuantityChange('weight', e.target.value)}
                    />
                  </div>
                  <div className="perkg-price-info">
                    Price per kg: {formatCurrency(getSelectedService()?.pricePerKg * getPriceMultiplier(getSelectedService()))}
                    {parseFloat(itemQuantities.weight) > 0 && (
                      <div style={{ marginTop: 4, fontWeight: 600 }}>
                        Total: {formatCurrency(parseFloat(itemQuantities.weight) * getSelectedService()?.pricePerKg * getPriceMultiplier(getSelectedService()))}
                      </div>
                    )}
                  </div>
                  <div className="add-items-action">
                    <Button
                      onClick={handleAddItems}
                      disabled={!(parseFloat(itemQuantities.weight) > 0)}
                    >
                      <Plus size={16} /> Add Service
                    </Button>
                  </div>
                </div>
              )}

              {/* For PerPair services */}
              {getSelectedService()?.pricingModel === 'PerPair' && (
                <div className="perpair-input-section">
                  <div className="form-group">
                    <label className="form-label">Number of Pairs</label>
                    <input
                      type="number"
                      className="form-input"
                      min="0"
                      placeholder="Enter number of pairs"
                      value={itemQuantities['pairs'] || ''}
                      onChange={(e) => handleQuantityChange('pairs', e.target.value)}
                    />
                  </div>
                  <div className="perpair-price-info">
                    Price per pair: {formatCurrency(getSelectedService()?.pricePerPair * getPriceMultiplier(getSelectedService()))}
                    {parseInt(itemQuantities.pairs, 10) > 0 && (
                      <div style={{ marginTop: 4, fontWeight: 600 }}>
                        Total: {formatCurrency(parseInt(itemQuantities.pairs, 10) * getSelectedService()?.pricePerPair * getPriceMultiplier(getSelectedService()))}
                      </div>
                    )}
                  </div>
                  <div className="add-items-action">
                    <Button
                      onClick={handleAddItems}
                      disabled={!(parseInt(itemQuantities.pairs, 10) > 0)}
                    >
                      <Plus size={16} /> Add Service
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Added Items Section */}
          {addedServiceItems.length > 0 && (
            <div className="added-items-section">
              <h4>Added Items</h4>
              <div className="added-items-list">
                {addedServiceItems.map((item, index) => (
                  <div key={index} className="added-item-row">
                    <div className="added-item-info">
                      <span className="added-item-name">{item.serviceCategoryName}</span>
                      <span className="added-item-type">{item.serviceType}</span>
                    </div>
                    <div className="added-item-details">
                      {item.pricingModel === 'PerKg' && `${item.weightInKg} kg`}
                      {item.pricingModel === 'PerPair' && `${item.pairCount} pair${item.pairCount === 1 ? '' : 's'}`}
                      {item.pricingModel === 'PerItem' && `${item.itemName}${item.garmentCode ? ` (${item.garmentCode})` : ''} × ${item.quantity}`}
                    </div>
                    <div className="added-item-price">
                      {formatCurrency(item.itemTotal)}
                    </div>
                    <button
                      className="remove-item-btn"
                      onClick={() => handleRemoveServiceItem(index)}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="added-items-total">
                <span>Total:</span>
                <span>{formatCurrency(calculateAddedItemsTotal())}</span>
              </div>
            </div>
          )}

          {renderAddonsSection(addedServiceAddons, setAddedServiceAddons)}
        </div>
      </Modal>
    </>
  );
}

export default Orders;
